import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { ServerControls } from '@/deploy/components/ui/ServerControls';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useDeployEnvStore } from '@/deploy/stores/deployEnv.store';
import { can } from '@/deploy/utils/permissions';
import {
  apiServicesService,
  type ApiService,
  type ApiCategory,
  type ApiHealthCheck,
  type ApiInstance,
  type ApiDeployResultEntry,
  type ApiReleaseResultEntry,
} from '@/deploy/services/api-services.service';
import { ApiError } from '@/deploy/services/api';
import { projectsService } from '@/deploy/services/projects.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import { ENVIRONMENT_BY_ID, type Project, type ProjectGroup } from '@/deploy/types';
import {
  API_ENVS, API_ENV_GROUPS, API_ENV_LABELS,
  apiCategory, apiEnvGroup, apiGroupLabel, canBeExternal, canonicalApiEnv, databaseNameMatches,
  deployedGroups, isDatabaseApi, isDatabaseImage, isExternalApi, normalizeVersion, sameVersion, versionStatus,
} from '@/deploy/utils/apiEnvs';
import {
  DB_SUFFIX_PATTERN, DEFAULT_INSTANCE, NONE_INSTANCE, NONE_INSTANCE_LABEL, apiInstances, endpointOn, findInstance,
  gameGroupsEnabled, groupsWithoutInstance, hasControllableServer, hasServer, instanceCitations, instanceDisplayName,
  instanceErrorMessage, instanceLabel, instancesOnEnv, instancesParam, instancesSupported, isDefaultInstance, linkOn,
  placeholderSlug, qualifiedPlaceholders, routingAvailable, runPool, serverKey, servedGroupsOf, versionOn,
  withoutInstances,
} from '@/deploy/utils/apiInstances';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { ApiFilesModal } from '@/deploy/components/ui/ApiFilesModal';
import {
  ApiInstanceLines, InstanceLinkNotice, InstanceTag, type InstancePending,
} from '@/deploy/components/deploy/api/ApiInstanceLines';
import { ApiInstanceModal, CopyConfigFields } from '@/deploy/components/deploy/api/ApiInstanceModal';
import { ApiInstanceDeleteModal } from '@/deploy/components/deploy/api/ApiInstanceDeleteModal';
import { ApiAttachServerModal } from '@/deploy/components/deploy/api/ApiAttachServerModal';
import { ApiCopyConfigModal } from '@/deploy/components/deploy/api/ApiCopyConfigModal';
import { ApiRoutingModal } from '@/deploy/components/deploy/api/ApiRoutingModal';
import { ApiRoutingMatrixModal } from '@/deploy/components/deploy/api/ApiRoutingMatrixModal';
import {
  DatabaseBadge, DatabaseConfirmField, DatabaseConfirmModal, databaseConfirmErrorMessage,
} from '@/deploy/components/deploy/api/DatabaseConfirm';
import {
  apiOverrideService,
  type ApiOverrideState,
  type ApiOverrideResult,
} from '@/deploy/services/api-override.service';
// Le redémarrage après un override est déclenché par le backend, plus par le panel.
import { serverControlService, type ServerScope } from '@/deploy/services/server-control.service';
import { POWER_META, powerDisplay } from '@/deploy/utils/powerState';
import styles from './ApiServices.module.scss';
import instStyles from '@/deploy/components/deploy/api/ApiInstances.module.scss';

/** Modale ouverte depuis une ligne : l'API est relue dans la liste à chaque rendu (rechargée après une action). */
type ApiDialog = (
  | { kind: 'deploy'; apiId: string; instances?: string[] }
  | { kind: 'files'; apiId: string; instance: string }
  | { kind: 'override'; apiId: string; instance: string }
  | { kind: 'routing'; apiId: string }
  | { kind: 'instance'; apiId: string; mode: 'create' | 'rename' | 'server'; instance?: string }
  | { kind: 'deleteInstance'; apiId: string; instance: string }
  | { kind: 'attach'; apiId: string; instance: string }
  | { kind: 'copyConfig'; apiId: string; instance: string }
) & {
  /**
   * Onglet Externe : groupe d'envs de la ligne, que la modale vise à la place de l'onglet d'env
   * (null = API externe déployée nulle part). Absent : l'env de l'onglet.
   */
  env?: ApiEnvId | null;
};

/** Ligne de l'onglet Externe : groupe d'envs où l'API tourne (null = nulle part) et ceux où elle tourne en trop. */
interface ExternalPlacement {
  group: ApiEnvId | null;
  others: ApiEnvId[];
}

/** État de Release d'une API, tenu par la page : il survit au changement d'onglet, qui démonte la ligne. */
interface RowRelease {
  pending: Record<string, InstancePending>;
  outcomes: Record<string, ReleaseOutcome>;
}

const NO_GROUPS: ProjectGroup[] = [];
const NO_RELEASE: RowRelease = { pending: {}, outcomes: {} };

// Onglet Externe : paramètre d'URL ?tab=externe, repris de la session quand on revient sur la
// page sans lui (lien de la sidebar). Il ne touche pas au store d'env, partagé avec d'autres pages.
const TAB_PARAM = 'tab';
const EXTERNAL_TAB = 'externe';
const TAB_STORAGE_KEY = 'recubebe-apis-tab';

function storedExternalTab(): boolean {
  try { return sessionStorage.getItem(TAB_STORAGE_KEY) === EXTERNAL_TAB; } catch { return false; }
}

function storeExternalTab(external: boolean): void {
  try {
    if (external) sessionStorage.setItem(TAB_STORAGE_KEY, EXTERNAL_TAB);
    else sessionStorage.removeItem(TAB_STORAGE_KEY);
  } catch { /* stockage indisponible : l'onglet ne tient que par l'URL */ }
}

/**
 * Groupes d'envs où une API externe tourne : version déployée connue, ou serveur de la
 * Principale présent d'après le lien live (image sans tag, version illisible). La garde du
 * CICD ne lit que la version : sans ce repli, rien ne bloquerait l'arrivée sur un 2e groupe.
 * `api` doit être la lecture brute (avec ses instances), aussi hors NationsGlory.
 */
function externalGroups(api: ApiService): ApiEnvId[] {
  const main = apiInstances(api)[0];
  const live = new Set(deployedGroups(api));
  return API_ENV_GROUPS.filter((g) => live.has(g) || hasControllableServer(main, g));
}

/** « A », « A et B », « A, B et C ». */
function joinFr(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

export function ApiServices() {
  const project = useSessionStore((s) => s.currentProject());
  const [loadedApis, setApis] = useState<ApiService[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Projet relu au CICD : groupes de jeu (absents d'un CICD plus ancien) et token public
  const [projectInfo, setProjectInfo] = useState<Project | null>(null);
  // Groupes de jeu, instances et répartition : NationsGlory seulement (drapeau du CICD). Ailleurs,
  // les API sont lues sans instances ni répartition et la page reste celle d'avant les instances.
  const gameGroups = gameGroupsEnabled(projectInfo ?? project);
  const groups = gameGroups ? projectInfo?.groups ?? NO_GROUPS : NO_GROUPS;
  const apis = useMemo(
    () => (gameGroups ? loadedApis : loadedApis.map(withoutInstances)),
    [loadedApis, gameGroups],
  );
  // Placement des API externes (ou qui le deviennent via Éditer) : lu sur la liste brute, dont
  // les liens live restent là hors NationsGlory.
  const placedGroups = useMemo(() => {
    const byId = new Map(loadedApis.map((a) => [a.id, externalGroups(a)]));
    return (api: ApiService): ApiEnvId[] => byId.get(api.id) ?? externalGroups(api);
  }, [loadedApis]);

  const envId = useDeployEnvStore((s) => s.env) as ApiEnvId;
  const setEnvId = useDeployEnvStore((s) => s.setEnv);
  // Dev et Bêta partagent les serveurs API : toutes les lectures passent par l'env canonique.
  const dataEnv = canonicalApiEnv(envId);
  // Onglet Externe : vue propre à cette page, l'env du store reste celui du dernier onglet d'env.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(TAB_PARAM);
  const externalView = tabParam === EXTERNAL_TAB || (tabParam === null && storedExternalTab());
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ApiService | null>(null);
  const [dialog, setDialog] = useState<ApiDialog | null>(null);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [releases, setReleases] = useState<Record<string, RowRelease>>({});

  const updateRelease = useCallback((apiId: string, fn: (r: RowRelease) => RowRelease) => {
    setReleases((all) => ({ ...all, [apiId]: fn(all[apiId] ?? NO_RELEASE) }));
  }, []);

  // Releases dont le CICD suit encore le démarrage (pull de l'image, boot, ip:port qui répond) :
  // leur issue est relue toutes les 3 s ici, la page tenant l'état d'une ligne démontée par un
  // changement d'onglet.
  const releasesRef = useRef(releases);
  releasesRef.current = releases;
  const verifyingIds = Object.entries(releases)
    .filter(([, r]) => Object.values(r.outcomes).some((o) => o.result?.verifying))
    .map(([id]) => id)
    .sort()
    .join(',');

  const setExternalView = (external: boolean) => {
    storeExternalTab(external);
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      if (external) n.set(TAB_PARAM, EXTERNAL_TAB);
      else n.delete(TAB_PARAM);
      return n;
    }, { replace: true });
  };

  // Onglet repris de la session : l'URL le dit aussi, pour qu'un rechargement ou un lien copié le garde.
  useEffect(() => {
    if (tabParam === null && storedExternalTab()) {
      setSearchParams((p) => {
        const n = new URLSearchParams(p);
        n.set(TAB_PARAM, EXTERNAL_TAB);
        return n;
      }, { replace: true });
    } else if (tabParam === EXTERNAL_TAB) {
      storeExternalTab(true);
    }
  }, [tabParam, setSearchParams]);

  // Suivis de démarrage tenus par le CICD (page rechargée ou rouverte, autre membre du staff) :
  // repris une fois par projet, au premier chargement de la liste. Le suivi de la page prend le relais.
  const seededProject = useRef<string | null>(null);
  const seedVerifications = useCallback(async (pid: string, list: ApiService[]) => {
    let byApi: Record<string, ApiReleaseResultEntry[]>;
    try {
      byApi = (await apiServicesService.projectReleaseVerifications(pid)).byApi ?? {};
    } catch {
      return; // CICD plus ancien (route absente) ou lecture KO : rien à reprendre
    }
    for (const api of list) {
      const fresh = byApi[api.id];
      if (!fresh?.length) continue;
      updateRelease(api.id, (r) => {
        const seeded = seededOutcomes(api, fresh, r);
        return Object.keys(seeded).length === 0 ? r : { ...r, outcomes: { ...seeded, ...r.outcomes } };
      });
    }
  }, [updateRelease]);

  const reload = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    setError(null);
    try {
      const list = await apiServicesService.list(project.id, { live: true });
      setApis(list);
      if (seededProject.current !== project.id) {
        seededProject.current = project.id;
        void seedVerifications(project.id, list);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [project.id, seedVerifications]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (!project.id || !verifyingIds) return;
    const pid = project.id;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      let concluded = false;
      await Promise.all(verifyingIds.split(',').map(async (apiId) => {
        let fresh: ApiReleaseResultEntry[];
        try {
          fresh = await readVerifications(pid, apiId);
        } catch (e) {
          // 404 (route absente, API supprimée) : l'issue ne viendra pas. Autre erreur : on réessaie.
          if (!(e instanceof ApiError) || e.status !== 404) return;
          fresh = [];
        }
        if (!alive) return;
        const updates = verificationUpdates(releasesRef.current[apiId]?.outcomes ?? {}, fresh);
        if (Object.keys(updates).length === 0) return;
        if (Object.values(updates).some((o) => !o.result?.verifying)) concluded = true;
        updateRelease(apiId, (r) => ({
          ...r,
          // Seules les issues encore en suivi sont remplacées
          outcomes: {
            ...r.outcomes,
            ...Object.fromEntries(Object.entries(updates).filter(([k]) => r.outcomes[k]?.result?.verifying)),
          },
        }));
      }));
      if (!alive) return;
      // Version et état live relus une fois l'issue connue
      if (concluded) void reload();
      timer = setTimeout(() => void tick(), 3000);
    };
    timer = setTimeout(() => void tick(), 3000);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [project.id, verifyingIds, updateRelease, reload]);

  useEffect(() => {
    if (!project.id) return;
    let alive = true;
    setProjectInfo(null);
    projectsService.get(project.id)
      .then((p) => { if (alive) setProjectInfo(p); })
      .catch(() => { /* ignore : pas de groupes ni de lien public */ });
    return () => { alive = false; };
  }, [project.id]);

  const syncAll = async () => {
    setSyncing(true);
    try {
      await apiServicesService.syncAll(project.id);
      await reload();
    } finally {
      setSyncing(false);
    }
  };

  const removeApi = async (api: ApiService) => {
    // Le CICD refuse tant qu'il reste une instance secondaire : on le dit sans rien demander.
    const others = apiInstances(api).filter((i) => !isDefaultInstance(i));
    if (others.length > 0) {
      alert(`${others.length > 1 ? 'Supprime d\'abord les instances' : 'Supprime d\'abord l\'instance'} ${joinFr(others.map(instanceLabel))}`
        + ` de ${api.name} (« Répartition », section Instances), avec ou sans leurs serveurs, puis l'API.`);
      return;
    }
    if (!confirm(`Supprimer l'API "${api.name}" ? (Le repo GitHub reste intact, seul le tracking Recube est retiré)`)) return;
    if (!gameGroups) {
      // Hors NationsGlory : comportement d'avant les instances, sans alerte en cas d'échec
      await apiServicesService.remove(project.id, api.id);
      await reload();
      return;
    }
    try {
      await apiServicesService.remove(project.id, api.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Suppression KO');
    }
    await reload();
  };

  const q = search.trim().toLowerCase();
  const matches = (a: ApiService) => !q ||
    a.name.toLowerCase().includes(q) ||
    a.repository.toLowerCase().includes(q) ||
    (a.dockerImage ?? '').toLowerCase().includes(q) ||
    apiInstances(a).some((i) => !isDefaultInstance(i) && instanceLabel(i).toLowerCase().includes(q));
  // Onglets d'env : API de jeu seulement. Les API externes (une seule instance, un seul groupe
  // d'envs) ont leur propre onglet, après Prod.
  const gameAll = apis.filter((a) => !isExternalApi(a));
  const externalAll = apis.filter(isExternalApi);
  const gameApis = gameAll.filter(matches);
  const externalApis = externalAll.filter(matches);
  // Déployée = au moins une instance a une version sur l'env (la racine ne porte que la Principale).
  const deployedCount = gameApis.filter((a) => apiInstances(a).some((i) => !!versionOn(i, dataEnv))).length;
  const externalDeployed = externalApis.filter((a) => placedGroups(a).length > 0).length;
  // CICD plus ancien : aucune catégorie renvoyée, toutes les API sont lues comme API de jeu.
  const categoryUnknown = apis.length > 0 && apis.every((a) => a.category === undefined);
  // Contrôle de santé : absent de toutes les API chargées = CICD qui ne le gère pas. Liste vide :
  // inconnu, le champ reste affiché et la réponse de la création tranche.
  const healthCheckUnsupported = apis.length > 0 && apis.every((a) => a.healthCheck === undefined);
  // Une ligne par groupe où l'API externe tourne : un seul en principe, plusieurs pour des
  // serveurs d'avant la règle du groupe unique (à nettoyer, chacun reste pilotable).
  const externalRows = externalApis.flatMap((api): { api: ApiService; placement: ExternalPlacement }[] => {
    const live = placedGroups(api);
    if (live.length === 0) return [{ api, placement: { group: null, others: [] } }];
    return live.map((g) => ({ api, placement: { group: g, others: live.filter((o) => o !== g) } }));
  });
  // Matrice Répartition : projet avec groupes et CICD qui gère les instances
  const routable = gameAll.filter(instancesSupported);
  const matrixAvailable = !externalView && groups.length > 0 && routable.length > 0;

  const closeDialog = () => setDialog(null);
  const dApi = dialog ? apis.find((a) => a.id === dialog.apiId) : undefined;
  // Env visé par la modale : celui de la ligne dans l'onglet Externe, sinon celui de l'onglet.
  const dEnv: ApiEnvId | undefined = dialog?.env === undefined ? envId : dialog.env ?? undefined;
  const dDataEnv = canonicalApiEnv(dEnv ?? envId);
  // Instance visée par Fichiers ou Override, relue à chaque rendu (undefined si elle a été supprimée)
  const dInstance = dApi && (dialog?.kind === 'files' || dialog?.kind === 'override')
    ? findInstance(dApi, dialog.instance) : undefined;

  const renderRow = (api: ApiService, placement?: ExternalPlacement) => (
    <ApiEnvRow
      key={placement ? `${api.id}|${placement.group ?? 'none'}` : api.id}
      api={api}
      envId={placement ? placement.group ?? 'dev' : envId}
      placement={placement}
      projectId={project.id}
      groups={groups}
      release={releases[api.id] ?? NO_RELEASE}
      onRelease={(fn) => updateRelease(api.id, fn)}
      onEdit={() => setEditTarget(api)}
      onDialog={setDialog}
      onDelete={() => void removeApi(api)}
      onSync={async () => { await apiServicesService.sync(project.id, api.id); await reload(); }}
      onReleased={reload}
    />
  );

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <nav className={styles.tabs} aria-label="Vue des API">
          {API_ENV_GROUPS.map((g) => {
            const members = apiEnvGroup(g);
            const active = !externalView && members.includes(envId);
            const tab = (e: ApiEnvId) => (
              <button
                key={e}
                type="button"
                // Dev et Bêta s'affichent ensemble : les deux sont pressés quand leur groupe est actif.
                aria-pressed={active}
                className={[styles.tab, active && members.length === 1 && styles.tabActive].filter(Boolean).join(' ')}
                onClick={() => { setEnvId(e); if (externalView) setExternalView(false); }}
              >
                <EnvBadge env={e} size="sm" />
                <span>{API_ENV_LABELS[e]}</span>
              </button>
            );
            if (members.length === 1) return tab(g);
            // Dev + Bêta : un seul bloc soudé, actif ensemble (même serveur API derrière).
            return (
              <div
                key={g}
                className={[styles.tabLinked, active && styles.tabLinkedActive].filter(Boolean).join(' ')}
                title="Dev et Bêta partagent les mêmes serveurs API"
              >
                {members.map((e, i) => (
                  <Fragment key={e}>
                    {i > 0 && <span className={styles.tabJoin} aria-hidden>+</span>}
                    {tab(e)}
                  </Fragment>
                ))}
              </div>
            );
          })}
          {/* Pas un env : la vue des API hors jeu, chacune sur son seul groupe d'envs. */}
          <span className={styles.tabSep} aria-hidden />
          <button
            type="button"
            aria-pressed={externalView}
            className={[styles.tab, styles.tabExternal, externalView && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => { if (!externalView) setExternalView(true); }}
            title="API externes (hors jeu) : une seule instance chacune, sur un seul groupe d'environnements"
          >
            <Icon name="external" size={13} />
            <span>Externe</span>
            <span className={styles.tabCount} aria-hidden>{externalAll.length}</span>
            <span className="sr-only">
              , {externalAll.length} {externalAll.length > 1 ? 'API externes' : 'API externe'}
            </span>
          </button>
        </nav>
        <div className={styles.headerActions}>
          <div className={styles.searchBox}>
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder={externalView ? 'Rechercher une API externe…' : 'Rechercher une API…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {matrixAvailable && (
            <Button variant="ghost" size="sm" leftIcon={<Icon name="git-branch" size={12} />}
              onClick={() => setMatrixOpen(true)}
              title={`Quelle instance de chaque API sert chaque groupe de jeu sur ${apiGroupLabel(envId)}`}>
              Répartition
            </Button>
          )}
          <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />}
            onClick={() => void syncAll()} loading={syncing}>
            Sync GitHub
          </Button>
          <Button variant="primary" leftIcon={<Icon name="plus" size={14} />}
            onClick={() => setCreateOpen(true)}>
            Créer une API
          </Button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {externalView ? (
        <section className={styles.panel}>
          <header className={styles.panelHeader}>
            <div className={styles.panelTitle}>
              <h2><Icon name="external" size={18} /> APIs externes</h2>
              <span className={styles.panelHint}>
                Hors jeu : une seule instance par API, sur un seul groupe d'environnements (Dev + Bêta, Pré-prod
                ou Prod), ignorée par la promotion
              </span>
            </div>
            <Badge tone="muted">{externalDeployed}/{externalApis.length} déployées</Badge>
          </header>
          <div className={styles.apiRows}>
            {loading && apis.length === 0 && <div className={styles.empty}>Chargement…</div>}
            {!loading && categoryUnknown && (
              <div className={styles.empty}>
                Ce CICD ne renvoie pas encore la catégorie des API : elles sont toutes traitées comme API de jeu,
                dans les onglets d'environnement. Cet onglet se remplira quand le CICD sera à jour.
              </div>
            )}
            {!loading && !categoryUnknown && externalAll.length === 0 && (
              <div className={styles.empty}>
                Aucune API externe. Une API hors jeu (outil, service tiers…) se crée avec la catégorie « Externe »,
                ou s'y range via « Éditer » : elle n'existe alors que sur un seul groupe d'environnements.
              </div>
            )}
            {!loading && externalAll.length > 0 && externalApis.length === 0 && (
              <div className={styles.empty}>Aucune API externe ne correspond à « {search.trim()} ».</div>
            )}
            {externalRows.map(({ api, placement }) => renderRow(api, placement))}
          </div>
        </section>
      ) : (
        <section className={styles.panel}>
          <header className={styles.panelHeader}>
            <h2><Icon name="zap" size={18} /> APIs {apiGroupLabel(envId)}</h2>
            <Badge tone="muted">{deployedCount}/{gameApis.length} déployées</Badge>
          </header>
          <div className={styles.apiRows}>
            {/* Chargement affiché seulement au 1er fetch ; un refresh (après action) garde les lignes visibles. */}
            {loading && apis.length === 0 && <div className={styles.empty}>Chargement…</div>}
            {!loading && apis.length === 0 && (
              <div className={styles.empty}>Aucune API pour ce projet. Clique sur « Créer une API ».</div>
            )}
            {!loading && apis.length > 0 && gameAll.length === 0 && (
              <div className={styles.empty}>Aucune API de jeu : toutes les API du projet sont dans l'onglet Externe.</div>
            )}
            {!loading && gameAll.length > 0 && gameApis.length === 0 && (
              <div className={styles.empty}>Aucune API ne correspond à « {search.trim()} ».</div>
            )}
            {gameApis.map((api) => renderRow(api))}
          </div>
        </section>
      )}

      <PublicApiLink token={projectInfo?.publicApiToken ?? null} groups={groups} external={externalView} />

      {createOpen && (
        <ApiFormModal
          projectId={project.id}
          defaultCategory={externalView ? 'external' : 'ig'}
          healthCheckUnsupported={healthCheckUnsupported}
          instancesEnabled={apis.some(instancesSupported)}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); void reload(); }}
        />
      )}
      {editTarget && (
        <ApiFormModal
          projectId={project.id}
          api={editTarget}
          liveGroups={placedGroups(editTarget)}
          instancesEnabled={instancesSupported(editTarget)}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); void reload(); }}
        />
      )}
      {dialog?.kind === 'deploy' && dApi && (
        <ApiDeployModal
          projectId={project.id}
          api={dApi}
          initialEnv={dEnv}
          groups={groups}
          liveGroups={placedGroups(dApi)}
          instances={dialog.instances}
          onClose={closeDialog}
          onDeployed={() => { closeDialog(); void reload(); }}
        />
      )}
      {dialog?.kind === 'files' && dApi && dInstance && (
        <ApiFilesModal
          projectId={project.id}
          api={dApi}
          initialEnv={dEnv}
          instance={dInstance}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'override' && dApi && dInstance && (
        <ApiOverrideModal
          projectId={project.id}
          api={dApi}
          instance={dInstance}
          onClose={closeDialog}
          onDeployed={() => { closeDialog(); void reload(); }}
        />
      )}
      {(dialog?.kind === 'files' || dialog?.kind === 'override') && dApi && !dInstance && (
        // Instance supprimée pendant que la modale était ouverte : jamais de repli sur la Principale
        <Modal open onClose={closeDialog} title="Instance supprimée" width="sm"
          footer={<Button variant="primary" onClick={closeDialog}>Fermer</Button>}>
          <p style={{ margin: 0, fontSize: 13 }}>
            L'instance « {dialog.instance} » de {dApi.name} n'existe plus
            {dialog.kind === 'files' ? " : ses fichiers ne sont plus accessibles d'ici." : " : son override n'est plus accessible d'ici."}
          </p>
        </Modal>
      )}
      {dialog?.kind === 'routing' && dApi && (
        <ApiRoutingModal projectId={project.id} api={dApi} envId={dDataEnv} groups={groups}
          onClose={closeDialog} onChanged={reload} />
      )}
      {dialog?.kind === 'instance' && dApi && (
        <ApiInstanceModal projectId={project.id} api={dApi} envId={dDataEnv} mode={dialog.mode}
          instanceKey={dialog.instance} onClose={closeDialog} onChanged={reload} />
      )}
      {dialog?.kind === 'deleteInstance' && dApi && (
        <ApiInstanceDeleteModal projectId={project.id} api={dApi} instanceKey={dialog.instance} groups={groups}
          onClose={closeDialog} onChanged={reload}
          onOpenRouting={routingAvailable(dApi, groups) ? () => setDialog({ kind: 'routing', apiId: dApi.id }) : undefined} />
      )}
      {dialog?.kind === 'attach' && dApi && (
        <ApiAttachServerModal projectId={project.id} api={dApi} instanceKey={dialog.instance} envId={dDataEnv}
          onClose={closeDialog} onChanged={reload} />
      )}
      {dialog?.kind === 'copyConfig' && dApi && (
        <ApiCopyConfigModal projectId={project.id} api={dApi} instanceKey={dialog.instance} envId={dDataEnv}
          onClose={closeDialog} />
      )}
      {matrixOpen && (
        <ApiRoutingMatrixModal projectId={project.id} apis={routable} envId={dataEnv} groups={groups}
          onClose={() => setMatrixOpen(false)} onChanged={reload} />
      )}
    </div>
  );
}

// ============================================================
// Panel discret en pied de page : lien vers l'endpoint semi-public
// qui expose (ip, port, version) pour n'importe quel script serveur.
// ============================================================
function PublicApiLink({ token, groups, external }: {
  token: string | null;
  groups: ProjectGroup[];
  /**
   * Onglet Externe : même URL (elle liste toutes les API du projet, de jeu comprises, et
   * `group` y garde son effet sur celles-ci), plus une note sur la place des API externes.
   */
  external?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (!token) return null;
  const base = (import.meta.env.VITE_DEPLOY_API_URL as string | undefined)
    ?? (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:8087` : '');
  const url = `${base}/api/public/${token}/apis?env=<ENV>${groups.length > 0 ? '&group=<GROUPE>' : ''}`;
  const copy = () => { void navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <details style={{
      marginTop: 12, padding: '10px 12px',
      background: 'var(--color-background-secondary)',
      border: '1px dashed var(--color-border)',
      borderRadius: 8, fontSize: 12, color: 'var(--color-text-muted)',
    }}>
      <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
        <Icon name="external" size={11} /> Endpoint semi-public (pour scripts serveurs)
      </summary>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ margin: 0 }}>
          Sans auth JWT, GET seulement, renvoie <code>{'{name, version, ip, port, endpoint}'}</code>
          {' '}pour chaque API deployée dans l'env demandé. Le token est stable par projet.
        </p>
        {groups.length > 0 && (
          <p style={{ margin: 0 }}>
            <code>group</code> est optionnel ({groups.map((g) => g.key).join(', ')}) : sans lui, chaque API renvoie son
            instance Principale ; avec lui, l'instance que la répartition de l'env donne à ce groupe. Une API dont la
            répartition met ce groupe sur « {NONE_INSTANCE_LABEL} » n'y figure pas (warning dans la réponse).
          </p>
        )}
        {external && (
          <p style={{ margin: 0 }}>
            Une API externe n'y figure que pour les envs de son groupe (<code>dev</code> ou <code>beta</code> pour
            Dev + Bêta), avec sa seule instance{groups.length > 0 ? <>, quel que soit <code>group</code></> : ''}.
          </p>
        )}
        <code style={{
          display: 'block', padding: '6px 8px',
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 4, fontFamily: 'var(--font-family-mono)', fontSize: 11,
          wordBreak: 'break-all', color: 'var(--color-text)',
        }}>{url}</code>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={copy}
            style={{ padding: '4px 10px', fontSize: 11,
              border: '1px solid var(--color-border)', background: 'var(--color-surface)',
              borderRadius: 4, cursor: 'pointer', color: 'var(--color-text)' }}>
            {copied ? 'Copié !' : 'Copier'}
          </button>
        </div>
      </div>
    </details>
  );
}

// ============================================================
// Card d'une API
// ============================================================
/** Dernière Release d'une instance lancée depuis la ligne, gardée pour le groupe d'envs où elle a tourné. */
interface ReleaseOutcome {
  env: ApiEnvId;
  instance: string;
  result?: ApiReleaseResultEntry;
  error?: string;
  /** Suivi du démarrage perdu par le CICD (redémarré entre-temps) : issue inconnue, pas un échec. */
  lost?: boolean;
}

/**
 * Issues relues sur /release/verifications pour les Releases encore en suivi de `outcomes`, par
 * clé d'état. Une Release en suivi absente de la réponse a perdu son suivi (CICD redémarré).
 */
function verificationUpdates(
  outcomes: Record<string, ReleaseOutcome>, fresh: ApiReleaseResultEntry[],
): Record<string, ReleaseOutcome> {
  const out: Record<string, ReleaseOutcome> = {};
  for (const [key, o] of Object.entries(outcomes)) {
    if (!o.result?.verifying) continue;
    const env = canonicalApiEnv(o.env);
    const x = fresh.find((f) => (f.instance ?? DEFAULT_INSTANCE) === o.instance
      && canonicalApiEnv(f.environment as ApiEnvId) === env);
    out[key] = x
      // Env de la demande gardé (Bêta reste Bêta), le CICD répond en env canonique
      ? { ...o, result: { ...x, environment: o.result.environment } }
      : {
        env: o.env, instance: o.instance, lost: true,
        error: "le CICD a perdu le suivi du démarrage (redémarré entre-temps ?) : vérifie l'état de l'API.",
      };
  }
  return out;
}

/** Clé d'état de Release par instance et par groupe d'envs. */
const pairKey = (instance: string, env: ApiEnvId) => `${instance}|${env}`;

/**
 * Lecture de /release/verifications partagée entre le suivi de la page et l'attente des Releases
 * en cours (une par instance) : une lecture de moins de 2,5 s sert à tous.
 */
const verificationReads = new Map<string, { at: number; p: Promise<ApiReleaseResultEntry[]> }>();
function readVerifications(pid: string, apiId: string): Promise<ApiReleaseResultEntry[]> {
  const k = `${pid}|${apiId}`;
  const hit = verificationReads.get(k);
  if (hit && Date.now() - hit.at < 2500) return hit.p;
  const p = apiServicesService.releaseVerifications(pid, apiId).then((r) => r.results);
  verificationReads.set(k, { at: Date.now(), p });
  // Une lecture en échec n'est pas gardée : la suivante réessaie
  p.catch(() => { if (verificationReads.get(k)?.p === p) verificationReads.delete(k); });
  return p;
}

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * Attend l'issue d'une Release dont le CICD suit le démarrage (relue toutes les 3 s) : la Release
 * des instances n'arrête la suivante qu'une fois celle-ci relancée (pull de l'image et boot
 * compris), deux à la fois au plus. null : pas d'issue dans le délai du CICD (prolongé s'il le
 * publie) plus une minute, le suivi de la page prend le relais.
 */
async function awaitVerification(
  pid: string, apiId: string, key: string, started: ReleaseOutcome,
): Promise<ReleaseOutcome | null> {
  const t0 = Date.now();
  let deadline = t0 + ((started.result?.timeoutSeconds ?? 180) + 60) * 1000;
  while (Date.now() < deadline) {
    await sleep(3000);
    let fresh: ApiReleaseResultEntry[];
    try {
      fresh = await readVerifications(pid, apiId);
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 404) continue;
      fresh = [];
    }
    const next = verificationUpdates({ [key]: started }, fresh)[key];
    if (next && !next.result?.verifying) return next;
    // Worker sans port : le CICD prolonge son échéance au premier running et la publie, la place
    // reste prise jusque-là (jamais raccourcie, le CICD ne prolonge qu'une fois)
    const t = next?.result?.timeoutSeconds;
    if (t) deadline = Math.max(deadline, t0 + (t + 60) * 1000);
  }
  return null;
}

/**
 * Suivis du CICD repris dans l'état de la page, par clé d'état : ceux en cours, et les échecs dont
 * la version cible est toujours celle déployée (un échec réparé depuis par un Rollback ou un deploy
 * n'est pas repris). Rien ne remplace une Release que la page suit déjà.
 */
function seededOutcomes(
  api: ApiService, fresh: ApiReleaseResultEntry[], current: RowRelease,
): Record<string, ReleaseOutcome> {
  const out: Record<string, ReleaseOutcome> = {};
  const instances = apiInstances(api);
  for (const x of fresh) {
    const env = canonicalApiEnv(x.environment as ApiEnvId);
    const instance = x.instance ?? DEFAULT_INSTANCE;
    const key = pairKey(instance, env);
    if (out[key] || current.outcomes[key] || current.pending[key]) continue;
    const inst = instances.find((i) => i.key === instance);
    if (!inst) continue;
    const failedHere = !x.ok && !!x.toVersion && sameVersion(versionOn(inst, env), x.toVersion);
    if (x.verifying || failedHere) out[key] = { env, instance, result: x };
  }
  return out;
}

/** Options d'une Release lancée depuis la ligne ; `confirmDatabase` : nom retapé d'une API de base de données. */
interface ReleaseOptions { version?: string; force?: boolean; confirmDatabase?: string }

function ApiEnvRow({
  api, envId, placement, projectId, groups, release, onRelease, onEdit, onDialog: emitDialog, onDelete, onSync, onReleased,
}: {
  api: ApiService;
  /** Env de l'onglet ; onglet Externe : groupe de la ligne (sans objet si l'API n'est déployée nulle part). */
  envId: ApiEnvId;
  /** Onglet Externe seulement : groupe d'envs de l'API, affiché sur la ligne. */
  placement?: ExternalPlacement;
  projectId: string;
  groups: ProjectGroup[];
  release: RowRelease;
  onRelease: (fn: (r: RowRelease) => RowRelease) => void;
  onEdit: () => void;
  onDialog: (d: ApiDialog) => void;
  onDelete: () => void;
  onSync: () => Promise<void>;
  onReleased: () => Promise<void>;
}) {
  const [syncing, setSyncing] = useState(false);
  const [restarting, setRestarting] = useState(false);
  // API de base de données : Release (ou Rollback) en attente du nom retapé
  const [dbRelease, setDbRelease] = useState<{ targets: ApiInstance[]; opts: ReleaseOptions } | null>(null);
  // Release tenue par la page : une Release lancée ici reste suivie si on change d'onglet entre-temps.
  const { pending, outcomes } = release;
  const setPending = (fn: (p: Record<string, InstancePending>) => Record<string, InstancePending>) =>
    onRelease((r) => ({ ...r, pending: fn(r.pending) }));
  const setOutcomes = (fn: (o: Record<string, ReleaseOutcome>) => Record<string, ReleaseOutcome>) =>
    onRelease((r) => ({ ...r, outcomes: fn(r.outcomes) }));
  // Onglet Externe : chaque modale vise le groupe de la ligne, pas l'onglet d'env du store.
  const onDialog = (d: ApiDialog) => emitDialog(placement ? { ...d, env: placement.group } : d);

  const dataEnv = canonicalApiEnv(envId);
  const groupLabel = apiGroupLabel(envId);
  const latest = api.latestAvailable;
  // API externe déployée nulle part : ni serveur ni fichiers, seulement le choix de son groupe.
  const unplaced = placement?.group === null;

  // Instances : une API à instance unique garde la ligne d'avant, lue sur la Principale (= racine).
  const all = apiInstances(api);
  const multiInstance = all.length > 1;
  const present = instancesOnEnv(api, dataEnv, groups);
  const multi = present.length >= 2;
  const main = present.length === 1 ? present[0] : all[0];
  const mainServed = servedGroupsOf(api, main, dataEnv, groups);
  // Groupes réglés sur « Aucune » sur l'env : leurs serveurs ne reçoivent pas l'API (placeholders bruts)
  const withoutInstance = isExternalApi(api) ? [] : groupsWithoutInstance(api, dataEnv, groups);

  const deployed = multi ? undefined : versionOn(main, dataEnv);
  // Semver, pas une simple différence : une version déployée plus récente (préversion, tag
  // manuel) ne doit pas proposer une « Release » qui serait un downgrade.
  const status = versionStatus(deployed, latest);
  const outdated = status === 'behind';
  const endpoint = multi ? undefined : endpointOn(main, dataEnv); // "ip:port" du serveur Ptero (live=true)

  // Plusieurs instances : Release de celles qui ont un serveur utilisable et sont en retard.
  const isBehind = (i: ApiInstance) => versionStatus(versionOn(i, dataEnv), latest) === 'behind';
  const withServer = present.filter((i) => hasServer(i, dataEnv));
  const lagging = withServer.filter(isBehind);
  // En retard mais bloquées (plusieurs serveurs possibles) : ni Release ni « À jour » tant que le bon n'est pas rattaché
  const blocked = present.filter((i) => linkOn(i, dataEnv) === 'ambiguous' && isBehind(i));
  const readable = present.filter((i) => hasControllableServer(i, dataEnv));
  const multiVersions = [...new Set(present.map((i) => normalizeVersion(versionOn(i, dataEnv))).filter(Boolean))];
  const multiAhead = readable.some((i) => versionStatus(versionOn(i, dataEnv), latest) === 'ahead');
  const multiDeployed = multiVersions.length > 0;
  const blockedNote = blocked.length > 0
    ? `${joinFr(blocked.map(instanceLabel))} : plusieurs serveurs Ptero ont l'image de ${api.name}, rattache le bon (Rattacher un serveur…) avant la Release`
    : '';

  // API externe : une seule instance, listée dans l'onglet Externe sur son groupe d'envs.
  const external = isExternalApi(api);
  // Base de données : Release et Rollback demandent de retaper son nom (changer l'image peut l'empêcher de redémarrer)
  const database = isDatabaseApi(api);

  // Une Release à la fois par API : la ligne reste bloquée même si on change d'onglet entre-temps,
  // et tant que le CICD suit le démarrage d'une instance (il garde son serveur verrouillé).
  const busy = Object.keys(pending).length > 0 || Object.values(outcomes).some((o) => o.result?.verifying);
  const pendingOf = (inst: ApiInstance): InstancePending | undefined => {
    const key = pairKey(inst.key, dataEnv);
    return pending[key] ?? (outcomes[key]?.result?.verifying ? 'verifying' : undefined);
  };
  const outcomeOf = (inst: ApiInstance) => outcomes[pairKey(inst.key, dataEnv)] ?? null;
  const releasing = pendingOf(main) !== undefined;
  const runningHere = (multi ? present : [main]).filter((i) => pendingOf(i) === 'running');
  const laggingBusy = lagging.some((i) => pendingOf(i) !== undefined);
  const shownOutcome = outcomeOf(main);
  const routing = routingAvailable(api, groups);

  /**
   * Release de `targets` sur l'env de l'onglet : une requête par instance, deux à la fois au
   * plus, chacune avec son résultat. La version affichée est envoyée telle quelle, pour qu'une
   * release publiée entre-temps sur GitHub ne parte pas à sa place.
   */
  const runReleases = async (targets: ApiInstance[], opts: ReleaseOptions = {}) => {
    if (targets.length === 0) return;
    // Base de données : le nom retapé tient lieu de confirmation, demandé avant tout appel
    if (database && !opts.confirmDatabase) {
      setDbRelease({ targets, opts });
      return;
    }
    const env = dataEnv;
    const version = opts.version ?? latest;
    const target = normalizeVersion(version);
    const verb = opts.force ? 'Rollback' : 'Release';
    if (!database && ENVIRONMENT_BY_ID[env].requiresConfirmation) {
      const question = !multiInstance
        ? `${verb} de ${api.name} en v${target} sur ${groupLabel} : l'API sera arrêtée puis redémarrée. Continuer ?`
        : targets.length === 1
          ? `${verb} de ${instanceDisplayName(api, targets[0])} en v${target} sur ${groupLabel} : l'instance sera arrêtée puis redémarrée. Continuer ?`
          : `${verb} de ${api.name} en v${target} sur ${groupLabel} pour ${joinFr(targets.map(instanceLabel))} : chaque instance sera arrêtée puis redémarrée, deux à la fois au plus. Continuer ?`;
      if (!confirm(question)) return;
    }
    const keys = targets.map((i) => pairKey(i.key, env));
    setPending((p) => ({ ...p, ...Object.fromEntries(keys.map((k) => [k, 'queued' as const])) }));
    // Instance unique : un seul résultat gardé, comme avant les instances (celui d'un autre onglet
    // disparaît). Plusieurs instances : seuls les résultats des instances relancées sur cet env.
    setOutcomes((o) => (multiInstance
      ? Object.fromEntries(Object.entries(o).filter(([k]) => !keys.includes(k)))
      : {}));
    const flags = { needsCreate: false };
    await runPool(targets, 2, async (inst) => {
      const key = pairKey(inst.key, env);
      setPending((p) => ({ ...p, [key]: 'running' }));
      let started: ReleaseOutcome | null = null;
      try {
        const res = await apiServicesService.release(projectId, api.id, {
          environments: [envId],
          version: version || undefined,
          force: opts.force || undefined,
          instances: instancesParam(api, [inst.key]),
          confirmDatabase: opts.confirmDatabase,
        });
        // Un résultat sans `instance` (CICD plus ancien) est celui de la Principale.
        const mine = res.results.filter((x) => (x.instance ?? DEFAULT_INSTANCE) === inst.key);
        const r = mine.find((x) => apiEnvGroup(env).includes(x.environment as ApiEnvId)) ?? mine[0] ?? res.results[0];
        const outcome: ReleaseOutcome = r
          ? { env, instance: inst.key, result: r }
          : { env, instance: inst.key, error: 'Réponse vide du CICD' };
        setOutcomes((o) => ({ ...o, [key]: outcome }));
        if (r?.verifying) started = outcome;
        if (r?.needsCreate) flags.needsCreate = true;
      } catch (e) {
        setOutcomes((o) => ({
          ...o,
          [key]: { env, instance: inst.key, error: databaseConfirmErrorMessage(e, api) ?? releaseErrorMessage(e) },
        }));
      } finally {
        setPending((p) => Object.fromEntries(Object.entries(p).filter(([k]) => k !== key)));
      }
      // Start envoyé, le CICD répond tout de suite : la place reste prise jusqu'à l'issue du
      // démarrage, sinon l'instance suivante serait arrêtée pendant que celle-ci démarre encore.
      if (!started) return;
      const final = await awaitVerification(projectId, api.id, key, started);
      if (!final) return;
      setOutcomes((o) => (o[key]?.result?.verifying ? { ...o, [key]: final } : o));
      void onReleased();
    });
    // Pas de serveur sur ce groupe (API à instance unique) : la création passe par la modale de déploiement.
    if (flags.needsCreate && !multiInstance) onDialog({ kind: 'deploy', apiId: api.id });
    await onReleased();
  };

  /** Confirmation d'une Release ou d'un Rollback d'une API de base de données : nom retapé, puis la Release. */
  const renderDbRelease = () => {
    if (!dbRelease) return null;
    const { targets, opts } = dbRelease;
    const target = normalizeVersion(opts.version ?? latest);
    const verb = opts.force ? 'Rollback' : 'Release';
    const who = multiInstance ? ` (${joinFr(targets.map(instanceLabel))})` : '';
    return (
      <DatabaseConfirmModal
        api={api}
        title={`${verb} de ${api.name}`}
        summary={<>
          {opts.force ? 'Le Rollback' : 'La Release'} en v{target || '?'} sur {groupLabel}{who} change l'image
          de <code>{api.name}</code>.
        </>}
        confirmLabel={`${verb} v${target || '?'}`}
        onClose={() => setDbRelease(null)}
        onConfirm={() => {
          setDbRelease(null);
          void runReleases(targets, { ...opts, confirmDatabase: api.name });
        }}
      />
    );
  };

  const dismiss = (inst: ApiInstance) => {
    const key = pairKey(inst.key, dataEnv);
    setOutcomes((o) => Object.fromEntries(Object.entries(o).filter(([k]) => k !== key)));
  };

  // Plusieurs instances : un redémarrage par serveur, l'un après l'autre.
  const restartAll = async () => {
    const targets = withServer;
    if (ENVIRONMENT_BY_ID[dataEnv].requiresConfirmation
      && !confirm(`Redémarrer ${api.name} sur ${groupLabel} : ${joinFr(targets.map(instanceLabel))} ?`)) return;
    setRestarting(true);
    const errors: string[] = [];
    for (const inst of targets) {
      try {
        await serverControlService.power(projectId, `api-${dataEnv}` as ServerScope, serverKey(api, inst), 'restart');
      } catch (e) {
        errors.push(`${instanceLabel(inst)} : ${e instanceof Error ? e.message : 'erreur'}`);
      }
    }
    setRestarting(false);
    if (errors.length > 0) alert(`Redémarrage KO : ${errors.join(' · ')}`);
  };

  const detach = async (inst: ApiInstance) => {
    if (!confirm(`Détacher le serveur de ${instanceDisplayName(api, inst)} sur ${groupLabel} ? Il reste sur Ptero, sans lien avec l'API.`)) return;
    try {
      await apiServicesService.detachInstance(projectId, api.id, inst.key, dataEnv);
    } catch (e) {
      alert(instanceErrorMessage(e));
    }
    await onReleased();
  };

  const createServer = (inst: ApiInstance) =>
    onDialog({ kind: 'instance', apiId: api.id, mode: 'server', instance: inst.key });

  /** Menu d'une sous-ligne : actions sur cette seule instance. */
  const renderMenu = (inst: ApiInstance) => {
    const principal = isDefaultInstance(inst);
    const link = linkOn(inst, dataEnv);
    const usable = hasServer(inst, dataEnv);
    const controllable = hasControllableServer(inst, dataEnv);
    const behind = usable && versionStatus(versionOn(inst, dataEnv), latest) === 'behind';
    // La Principale ne reçoit jamais de copie : elle pointerait sur la base d'une autre instance (refusé par le CICD).
    const copySource = !principal && usable && all.some((o) => o.key !== inst.key && hasServer(o, dataEnv));
    return (<>
      {behind && latest && (
        <Button variant="softWarning" size="sm" leftIcon={<Icon name="rocket" size={11} />} disabled={busy}
          onClick={() => void runReleases([inst])}
          title={`Deploy de v${latest} puis arrêt et redémarrage de ${instanceDisplayName(api, inst)} sur ${groupLabel}`}>
          Release v{latest}
        </Button>
      )}
      <Button variant="softPrimary" size="sm" leftIcon={<Icon name="rocket" size={11} />} disabled={busy}
        onClick={() => onDialog({ kind: 'deploy', apiId: api.id, instances: [inst.key] })}
        title="Choisir une version précise pour cette instance (sans redémarrage automatique)">
        Déployer une version…
      </Button>
      {controllable && (
        <Button variant="ghost" size="sm" leftIcon={<Icon name="layers" size={11} />} disabled={busy}
          onClick={() => onDialog({ kind: 'files', apiId: api.id, instance: inst.key })}>
          Fichiers
        </Button>
      )}
      {controllable && dataEnv === 'dev' && (
        <Button variant="softPrimary" size="sm" leftIcon={<Icon name="package" size={11} />} disabled={busy}
          onClick={() => onDialog({ kind: 'override', apiId: api.id, instance: inst.key })}
          title="Déposer un build local qui remplacera l'API compilée de l'image, sur Dev + Bêta uniquement">
          Déployer DLL
        </Button>
      )}
      {copySource && (
        <Button variant="ghost" size="sm" leftIcon={<Icon name="download" size={11} />} disabled={busy}
          onClick={() => onDialog({ kind: 'copyConfig', apiId: api.id, instance: inst.key })}>
          Copier la config…
        </Button>
      )}
      {(link === 'none' || link === 'missing') && (
        <Button variant="softPrimary" size="sm" leftIcon={<Icon name="plus" size={11} />} disabled={busy}
          onClick={() => createServer(inst)}>
          Créer le serveur…
        </Button>
      )}
      {(link === 'none' || link === 'missing' || link === 'ambiguous' || link === 'broken') && (
        <Button variant="ghost" size="sm" leftIcon={<Icon name="server" size={11} />} disabled={busy}
          onClick={() => onDialog({ kind: 'attach', apiId: api.id, instance: inst.key })}>
          Rattacher un serveur…
        </Button>
      )}
      {!principal && (link === 'bound' || link === 'missing' || link === 'broken') && (
        <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={11} />} disabled={busy}
          onClick={() => void detach(inst)}>
          Détacher
        </Button>
      )}
      {!principal && (
        <Button variant="ghost" size="sm" leftIcon={<Icon name="pencil" size={11} />} disabled={busy}
          onClick={() => onDialog({ kind: 'instance', apiId: api.id, mode: 'rename', instance: inst.key })}>
          Renommer…
        </Button>
      )}
      {!principal && (
        <Button variant="softDanger" size="sm" leftIcon={<Icon name="x" size={11} />} disabled={busy}
          onClick={() => onDialog({ kind: 'deleteInstance', apiId: api.id, instance: inst.key })}>
          Supprimer l'instance
        </Button>
      )}
    </>);
  };

  /** Ligne de résultat de Release d'une instance (sous-ligne, ou ligne à instance unique). */
  const renderResult = (inst: ApiInstance, autoCreate: boolean) => {
    const state = pendingOf(inst);
    const oc = outcomeOf(inst);
    if (state !== 'running' && !oc) return null;
    return (
      <ReleaseResultLine
        releasing={state === 'running'}
        outcome={oc}
        groupLabel={groupLabel}
        endpoint={endpointOn(inst, dataEnv)}
        healthCheck={api.healthCheck}
        busy={busy}
        onRollback={(v) => void runReleases([inst], { version: v, force: true })}
        onDismiss={() => dismiss(inst)}
        onCreateServer={autoCreate ? undefined : () => createServer(inst)}
      />
    );
  };

  const announcement = runningHere.length > 0
    ? `Release de ${runningHere.map((i) => instanceDisplayName(api, i)).join(', ')} en cours sur ${groupLabel}.`
    : (multi ? present : [main])
      .map((i) => {
        const oc = outcomeOf(i);
        return oc ? `${instanceDisplayName(api, i)} : ${releaseAnnouncement(oc, groupLabel, !multiInstance)}` : '';
      })
      .filter(Boolean).join(' ');

  const endpointButton = endpoint && (
    <button
      type="button"
      className={styles.apiEndpoint}
      onClick={() => void navigator.clipboard.writeText(endpoint)}
      title="Copier l'adresse (IP:port)"
    >
      <Icon name="server" size={10} /> {endpoint}
    </button>
  );
  // Ligne à instance unique : drapeaux des groupes servis (projet avec groupes) et état du lien
  // (API à plusieurs instances) devant la pastille ; sinon la pastille seule, comme avant.
  // Les API externes n'ont ni drapeaux ni répartition.
  const soloExtras = !external && !multi && present.length === 1 && (groups.length > 0 || multiInstance);

  return (
    <div className={styles.apiRow}>
      <div className={styles.apiMain}>
        <span
          className={styles.apiChip}
          style={{ background: (multi ? multiDeployed : deployed) ? 'var(--color-primary)' : 'var(--color-text-muted)' }}
        >
          <Icon name={external ? 'external' : 'zap'} size={12} />
        </span>
        <div className={styles.apiInfo} style={multi ? { flex: '1 1 auto' } : undefined}>
          <div className={styles.apiName}>
            {api.name}
            {placement?.group && (
              <span className={styles.apiGroupTag} title={`Déployée sur ${groupLabel} : ses actions visent ce groupe`}>
                {apiEnvGroup(placement.group).map((e) => <EnvBadge key={e} env={e} size="sm" />)}
              </span>
            )}
            {database && <DatabaseBadge />}
            {multi ? (
              multiVersions.length === 1 ? (
                <Badge tone="info">v{multiVersions[0]}</Badge>
              ) : multiVersions.length > 1 ? (
                <span title={present.filter((i) => versionOn(i, dataEnv))
                  .map((i) => `${instanceLabel(i)} v${normalizeVersion(versionOn(i, dataEnv))}`).join(' · ')}>
                  <Badge tone="warning">versions mixtes</Badge>
                </span>
              ) : (
                <Badge tone="muted">Non déployée</Badge>
              )
            ) : deployed ? (
              <>
                <Badge tone="info">v{deployed}</Badge>
                {outdated && (
                  <Badge tone="warning"><Icon name="arrow-right" size={9} /> v{latest}</Badge>
                )}
              </>
            ) : placement?.group ? (
              // Onglet Externe : serveur présent sur le groupe, mais image sans version lisible.
              <span title={`Un serveur de ${api.name} tourne sur ${groupLabel}, sans version lisible dans son image : « Déployer » lui en donne une`}>
                <Badge tone="warning">version inconnue</Badge>
              </span>
            ) : (
              <Badge tone="muted">Non déployée</Badge>
            )}
            {multi && (lagging.length > 0 || blocked.length > 0) && (
              <Badge tone="warning"><Icon name="arrow-right" size={9} /> v{latest}</Badge>
            )}
          </div>
          <code className={styles.apiImage} title={api.dockerImage}>{api.dockerImage}</code>
          {placement && placement.others.length > 0 && (
            <span className={styles.apiRowHint}>
              Tourne aussi sur {joinFr(placement.others.map(apiGroupLabel))} : une API externe n'existe que sur un seul
              groupe d'environnements, supprime le serveur en trop.
            </span>
          )}
          {unplaced && (
            <span className={styles.apiRowHint}>
              Pas encore déployée : « Déployer » propose de choisir son unique groupe d'environnements.
            </span>
          )}
          {multi ? (
            <ApiInstanceLines
              api={api}
              projectId={projectId}
              envId={dataEnv}
              instances={present}
              groups={groups}
              pendingOf={pendingOf}
              renderMenu={renderMenu}
              renderResult={(inst) => renderResult(inst, false)}
              onCreateServer={createServer}
            />
          ) : soloExtras ? (
            <div className={instStyles.soloEndpoint}>
              <InstanceTag inst={main} served={mainServed} />
              {endpointButton}
              {multiInstance && (
                <InstanceLinkNotice api={api} inst={main} envId={dataEnv} served={mainServed}
                  onCreateServer={() => createServer(main)} />
              )}
              {groups.length > 0 && mainServed.length === 0 && hasControllableServer(main, dataEnv) && (
                <span className={instStyles.instNote}>
                  {isDefaultInstance(main) ? `ne sert que les serveurs sans groupe sur ${groupLabel}` : `ne sert aucun groupe sur ${groupLabel}`}
                </span>
              )}
            </div>
          ) : endpointButton}
          {withoutInstance.length > 0 && (
            <span className={[instStyles.instNote, instStyles.instTag].join(' ')}
              title={`Répartition « ${NONE_INSTANCE_LABEL} » sur ${groupLabel} : les serveurs de ${withoutInstance.length > 1 ? 'ces groupes' : 'ce groupe'}`
                + ` ne reçoivent pas ${api.name}, ses placeholders [[${placeholderSlug(api.name)}_HOST]] et`
                + ` [[${placeholderSlug(api.name)}_PORT]] restent non remplacés, comme`
                + ` ${qualifiedPlaceholders(api, withoutInstance.map((g) => g.key))} sur un serveur qui sert plusieurs`
                + ' groupes (le proxy)'}>
              {withoutInstance.map((g) => <GroupBadge key={g.key} group={g} />)}
              sans {api.name} sur {groupLabel} (« {NONE_INSTANCE_LABEL} »)
            </span>
          )}
        </div>
      </div>

      <div className={styles.apiControls}>
        {multi && withServer.length > 0 ? (
          <Button variant="softWarning" size="sm" leftIcon={<Icon name="refresh" size={12} />}
            loading={restarting} disabled={busy || restarting} onClick={() => void restartAll()}
            title={`Redémarre ${joinFr(withServer.map(instanceLabel))}, l'un après l'autre`}>
            Redémarrer ({withServer.length})
          </Button>
        ) : !multi && deployed ? (
          <ServerControls
            projectId={projectId}
            scope={`api-${dataEnv}` as ServerScope}
            targetKey={serverKey(api, main)}
            sourceName={instanceDisplayName(api, main)}
            sourceSubtitle={`${groupLabel} · v${deployed}`}
            icon="zap"
            disabled={busy}
          />
        ) : (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Icon name="rocket" size={12} />}
            onClick={() => onDialog({ kind: 'deploy', apiId: api.id, instances: present.length > 0 ? present.map((i) => i.key) : undefined })}
            disabled={!latest || busy}
            title={!latest ? 'Aucune release GitHub détectée'
              : unplaced ? `Déployer v${latest} sur un seul groupe d'environnements, à choisir : Dev + Bêta, Pré-prod ou Prod`
                : `Déployer v${latest} sur ${groupLabel}`}
          >
            {latest ? `Déployer v${latest}` : 'Aucune release'}
          </Button>
        )}
      </div>

      <div className={styles.apiActions}>
        {!multi && deployed && latest && (outdated ? (
          <Button variant="softWarning" size="sm" leftIcon={<Icon name="rocket" size={11} />}
            loading={releasing} disabled={busy} onClick={() => void runReleases([main])}
            title={`Deploy de v${latest} puis arrêt et redémarrage de l'API sur ${groupLabel}`}>
            {releasing ? 'Release en cours…' : `Release v${latest}`}
          </Button>
        ) : status === 'ahead' ? (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="check" size={11} />} disabled
            title={`v${deployed} est plus récente que la dernière release GitHub (v${latest}) : pas de Release proposée`}>
            Plus récente que v{latest}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="check" size={11} />} disabled
            title={`v${deployed} est la dernière release GitHub`}>
            À jour
          </Button>
        ))}
        {multi && latest && (lagging.length > 0 ? (
          <Button variant="softWarning" size="sm" leftIcon={<Icon name="rocket" size={11} />}
            loading={laggingBusy} disabled={busy}
            onClick={() => void runReleases(lagging)}
            title={`Deploy de v${latest} puis arrêt et redémarrage sur ${groupLabel}, deux instances à la fois au plus : ${joinFr(lagging.map(instanceLabel))}${blockedNote ? `. Non comprises : ${blockedNote}` : ''}`}>
            {laggingBusy ? 'Release en cours…' : `Release v${latest}${lagging.length > 1 ? ` (${lagging.length} instances)` : ''}`}
          </Button>
        ) : blocked.length > 0 ? (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="warn" size={11} />} disabled title={blockedNote}>
            {blocked.length > 1 ? `${blocked.length} instances bloquées` : '1 instance bloquée'}
          </Button>
        ) : readable.some((i) => versionOn(i, dataEnv)) && (multiAhead ? (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="check" size={11} />} disabled
            title={`Une instance est plus récente que la dernière release GitHub (v${latest}) : pas de Release proposée`}>
            Plus récente que v{latest}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="check" size={11} />} disabled
            title={`Toutes les instances de ${groupLabel} sont en v${latest}`}>
            À jour
          </Button>
        )))}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={11} />} disabled={busy}
          loading={syncing} onClick={async () => { setSyncing(true); try { await onSync(); } finally { setSyncing(false); } }}>
          Sync
        </Button>
        {routing && (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="git-branch" size={11} />} disabled={busy}
            onClick={() => onDialog({ kind: 'routing', apiId: api.id })}
            title={`Quelle instance de ${api.name} sert chaque groupe de jeu sur ${groupLabel}`}>
            Répartition
          </Button>
        )}
        {!multi && !unplaced && (
          <Button variant="ghost" size="sm" leftIcon={<Icon name="layers" size={11} />} disabled={busy}
            onClick={() => onDialog({ kind: 'files', apiId: api.id, instance: main.key })}>
            Fichiers
          </Button>
        )}
        {/* Dev only côté UI (donc aussi Bêta, même serveur) ; le backend refuse aussi tout autre env. */}
        {!multi && !unplaced && dataEnv === 'dev' && (
          <Button variant="softPrimary" size="sm" leftIcon={<Icon name="package" size={11} />}
            onClick={() => onDialog({ kind: 'override', apiId: api.id, instance: main.key })}
            disabled={busy}
            title="Déposer un build local qui remplacera l'API compilée de l'image, sur Dev + Bêta uniquement">
            Déployer DLL
          </Button>
        )}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={11} />} onClick={onEdit} disabled={busy}>
          Éditer
        </Button>
        {(multi ? multiDeployed : !!deployed) && (
          <Button variant="softPrimary" size="sm" leftIcon={<Icon name="rocket" size={11} />}
            onClick={() => onDialog({ kind: 'deploy', apiId: api.id, instances: (multi ? present : [main]).map((i) => i.key) })}
            disabled={busy} title="Choisir une version précise (sans redémarrage automatique)">
            Déployer une version…
          </Button>
        )}
        <Button variant="softDanger" size="sm" leftIcon={<Icon name="x" size={11} />} onClick={onDelete} disabled={busy}>
          Supprimer
        </Button>
      </div>

      {/* Annonce lecteur d'écran : région persistante, sinon l'insertion de la ligne n'est pas lue. */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
      {!multi && (releasing || shownOutcome) && renderResult(main, !multiInstance)}
      {renderDbRelease()}
    </div>
  );
}

/** Workflow "Release" en échec : l'image ne viendra pas sans le relancer (CICD ancien : déduit du message). */
function imageFailed(r: ApiReleaseResultEntry): boolean {
  return r.imageFailed ?? /échoué/i.test(r.error ?? '');
}

/**
 * Résumé textuel de l'issue d'une Release, pour la région aria-live de la ligne. `autoCreate` :
 * sans serveur, la modale de déploiement s'ouvre d'elle-même (API à instance unique).
 */
function releaseAnnouncement(outcome: ReleaseOutcome, groupLabel: string, autoCreate = true): string {
  const r = outcome.result;
  if (!r && outcome.lost) return `Issue de la Release inconnue sur ${groupLabel} : ${outcome.error ?? ''}`;
  if (!r) return `Release en échec : ${outcome.error ?? 'erreur inconnue'}`;
  const to = r.toVersion ? `v${normalizeVersion(r.toVersion)}` : 'v?';
  const from = r.fromVersion ? `v${normalizeVersion(r.fromVersion)}` : 'v?';
  if (r.verifying) {
    return `Release lancée sur ${groupLabel}, ${from} vers ${to} : démarrage en cours`
      + `${r.timeoutSeconds ? `, vérifié pendant ${fmtSeconds(r.timeoutSeconds)} au plus` : ''}.`;
  }
  if (r.ok && r.upToDate) return `déjà à jour en ${r.toVersion ? to : from} sur ${groupLabel}.`;
  if (r.ok) {
    return `Release terminée sur ${groupLabel}, ${from} vers ${to}`
      + `${r.powerState ? `, état ${POWER_META[powerDisplay(r.powerState)].label.toLowerCase()}` : ''}.`;
  }
  if (r.imageNotReady) {
    return imageFailed(r)
      ? `Release impossible : le workflow Release de ${to} a échoué, image non publiée.`
      : `Release impossible : image ${to} pas encore publiée, réessaie dans quelques minutes.`;
  }
  if (r.needsCreate) {
    return autoCreate
      ? `pas de serveur sur ${groupLabel}, la modale de déploiement est ouverte.`
      : `pas de serveur sur ${groupLabel}.`;
  }
  const rollback = r.rollbackTo ? ` Rollback v${normalizeVersion(r.rollbackTo)} disponible.` : '';
  return `Release en échec sur ${groupLabel} : ${r.error ?? 'erreur inconnue'}.${rollback}`;
}

/** Message d'erreur d'une Release qui n'a pas pu répondre normalement. */
function releaseErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    // CICD antérieur à la route /release (il se redéploie à chaque push sur main).
    if ((e.status === 404 && !/introuvable/i.test(e.message)) || e.status === 405) {
      return "Release indisponible sur ce CICD (version antérieure) : utilise « Déployer une version… » puis redémarre l'API.";
    }
    if (e.status === 502 || e.status === 503 || e.status === 504) {
      return `Pas de réponse du CICD (HTTP ${e.status}) : la Release a pu continuer côté serveur, vérifie l'état de l'API.`;
    }
    return e.message;
  }
  // fetch coupé (proxy, réseau) : la requête a pu aboutir côté CICD.
  if (e instanceof TypeError) {
    return "Connexion au CICD perdue pendant la Release : elle a pu continuer côté serveur, vérifie l'état de l'API.";
  }
  return e instanceof Error ? e.message : 'Erreur';
}

/** "42 s", "3 min", "1 min 05". */
function fmtSeconds(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s} s`;
  const rest = s % 60;
  return rest === 0 ? `${s / 60} min` : `${Math.floor(s / 60)} min ${String(rest).padStart(2, '0')}`;
}

/** Badge d'état Ptero, libellés partagés avec les contrôles serveur. */
function PowerBadge({ state }: { state: string }) {
  const meta = POWER_META[powerDisplay(state)];
  return <Badge tone={meta.tone} dot>{meta.label}</Badge>;
}

/** Ligne de résultat sous l'API : Release en cours, suivi du démarrage, puis son issue (et le Rollback si besoin). */
function ReleaseResultLine({
  releasing, outcome, groupLabel, endpoint, healthCheck, busy, onRollback, onDismiss, onCreateServer,
}: {
  releasing: boolean;
  outcome: ReleaseOutcome | null;
  groupLabel: string;
  /** ip:port de l'instance sur l'env (liste live), cité pendant le suivi du démarrage. */
  endpoint?: string;
  /** Contrôle de santé de l'API : "process" = worker sans port, validé sur une marche sans redémarrage. */
  healthCheck?: ApiHealthCheck;
  busy: boolean;
  onRollback: (version: string) => void;
  onDismiss: () => void;
  /** Sous-ligne d'instance : « Créer le serveur… » au lieu d'ouvrir la modale de déploiement. */
  onCreateServer?: () => void;
}) {
  const dismiss = (
    <button type="button" className={styles.resultDismiss} onClick={onDismiss} title="Masquer" disabled={busy}>
      <Icon name="x" size={11} />
    </button>
  );

  if (releasing) {
    return (
      <div className={[styles.resultRow, styles.resultSkip, styles.apiRowResult].join(' ')}>
        <Icon name="refresh" size={12} />
        <span style={{ flex: 1 }}>
          Release en cours sur {groupLabel} : sync GitHub, changement d'image, arrêt puis redémarrage
          (jusqu'à 1 min 30 environ), puis suivi du démarrage.
        </span>
      </div>
    );
  }
  if (!outcome) return null;

  if (!outcome.result && outcome.lost) {
    return (
      <div className={[styles.resultRow, styles.resultWarn, styles.apiRowResult].join(' ')}>
        <Icon name="warn" size={12} />
        <span style={{ flex: 1 }}>Issue inconnue : {outcome.error}</span>
        {dismiss}
      </div>
    );
  }
  if (!outcome.result) {
    return (
      <div className={[styles.resultRow, styles.resultKo, styles.apiRowResult].join(' ')}>
        <Icon name="x" size={12} />
        <span style={{ flex: 1 }}>Release KO : {outcome.error}</span>
        {dismiss}
      </div>
    );
  }

  const r = outcome.result;
  const from = r.fromVersion ? `v${normalizeVersion(r.fromVersion)}` : 'v?';
  const to = r.toVersion ? `v${normalizeVersion(r.toVersion)}` : 'v?';

  // Start envoyé : le CICD attend l'issue réelle (starting pendant le pull et le boot n'est pas un échec)
  if (r.verifying) {
    return (
      <>
        <div className={[styles.resultRow, styles.resultSkip, styles.apiRowResult].join(' ')}>
          <Icon name="clock" size={12} />
          <span style={{ flex: 1 }}>
            {from} → {to} : démarrage en cours
            {/* Juste après le start, Wings reste offline le temps de préparer le serveur : pas un arrêt */}
            {r.powerState && (powerDisplay(r.powerState) === 'offline'
              ? <> · préparation du serveur</>
              : <> · <PowerBadge state={r.powerState} /></>)}
            <span className={styles.resultDetail}>
              {' '}{fmtSeconds(r.waitedSeconds ?? 0)}
              {r.timeoutSeconds ? ` sur ${fmtSeconds(r.timeoutSeconds)} max` : ''}. Pull de l'image puis boot :
              {healthCheck === 'process'
                ? ' worker sans port, la Release est validée après environ 1 min de marche sans redémarrage.'
                : ` la Release est validée dès que l'API répond${endpoint ? ` sur ${endpoint}` : ''}.`}
            </span>
          </span>
        </div>
        {r.warning && (
          <div className={[styles.resultRow, styles.resultWarn, styles.apiRowResult].join(' ')}>
            <Icon name="warn" size={12} />
            <span style={{ flex: 1 }}>{r.warning}</span>
          </div>
        )}
      </>
    );
  }
  const soft = !r.ok && (r.needsCreate || r.imageNotReady);
  const rollbackTo = r.ok ? undefined : r.rollbackTo;
  return (
    <>
      <div className={[styles.resultRow, r.ok ? styles.resultOk : soft ? styles.resultSkip : styles.resultKo, styles.apiRowResult].join(' ')}>
        <Icon name={r.ok ? 'check' : soft ? 'warn' : 'x'} size={12} />
        <span style={{ flex: 1 }}>
          {r.ok && r.upToDate && <>Déjà à jour ({r.toVersion ? to : from})</>}
          {r.ok && !r.upToDate && (
            <>
              {from} → {to}
              {r.powerState && <> · <PowerBadge state={r.powerState} /></>}
            </>
          )}
          {!r.ok && r.imageNotReady && (imageFailed(r) ? (
            <>
              Le workflow Release de {to} a échoué : l'image n'a pas été publiée. Relance le workflow sur GitHub puis réessaie.
              {r.error && <span className={styles.resultDetail}> {r.error}</span>}
            </>
          ) : (
            <>{r.error ?? `Image ${to} pas encore publiée, réessaie dans quelques minutes.`}</>
          ))}
          {!r.ok && !r.imageNotReady && r.needsCreate && (onCreateServer
            ? <>Pas de serveur sur {groupLabel}{r.error ? ` : ${r.error}` : '.'}</>
            : <>Pas de serveur sur {groupLabel} : crée-le depuis la modale de déploiement.</>)}
          {!r.ok && !soft && <>Échec {from} → {to} : {r.error ?? 'erreur inconnue'}</>}
        </span>
        {!r.ok && !r.imageNotReady && r.needsCreate && onCreateServer && (
          <Button variant="softPrimary" size="sm" leftIcon={<Icon name="plus" size={11} />} disabled={busy}
            onClick={onCreateServer}>
            Créer le serveur…
          </Button>
        )}
        {rollbackTo && (
          <Button variant="softDanger" size="sm" leftIcon={<Icon name="refresh" size={11} />} disabled={busy}
            onClick={() => onRollback(rollbackTo)}
            title={`Relancer une Release forcée en v${normalizeVersion(rollbackTo)}`}>
            Rollback v{normalizeVersion(rollbackTo)}
          </Button>
        )}
        {dismiss}
      </div>
      {r.warning && (
        <div className={[styles.resultRow, styles.resultWarn, styles.apiRowResult].join(' ')}>
          <Icon name="warn" size={12} />
          <span style={{ flex: 1 }}>{r.warning}</span>
        </div>
      )}
    </>
  );
}

// ============================================================
// Create/Edit form
// ============================================================

/**
 * Adresses où le CICD voit l'API répondre en ce moment (contrôle de santé "port", running depuis
 * plus que la grâce de boot sans « Erreur ») : « 172.27.2.20:25610 (Prod) ». Lu seulement quand
 * `active` (passage d'une API en "process"), une fois par ouverture.
 */
function useRespondingEndpoints(projectId: string, api: ApiService | undefined, active: boolean): string[] {
  const [found, setFound] = useState<string[]>([]);
  // Une seule lecture : la liste rechargée change l'objet api sans changer la réponse
  const started = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!active || started.current || !api) return;
    started.current = true;
    const targets = apiInstances(api).flatMap((inst) => API_ENV_GROUPS
      .filter((env) => !!endpointOn(inst, env))
      .map((env) => ({ inst, env })));
    void Promise.allSettled(targets.map(async ({ inst, env }) => {
      const st = await serverControlService.state(projectId, `api-${env}` as ServerScope, serverKey(api, inst));
      // Grâce de boot du CICD (60 s par défaut) passée et pas d'« Erreur » : la sonde a vu le port ouvert
      const open = st.status === 'running' && !!st.endpoint && (st.uptimeMs ?? 0) >= 60_000
        && st.healthCheck !== 'process';
      return open ? `${st.endpoint} (${apiGroupLabel(env)}${isDefaultInstance(inst) ? '' : `, ${instanceLabel(inst)}`})` : null;
    })).then((rs) => {
      if (!mounted.current) return;
      setFound(rs.flatMap((r) => (r.status === 'fulfilled' && r.value ? [r.value] : [])));
    });
  }, [active, api, projectId]);
  return active ? found : [];
}

function ApiFormModal({
  projectId, api, liveGroups: liveGroupsProp, defaultCategory, healthCheckUnsupported, instancesEnabled, onClose, onSaved,
}: {
  projectId: string;
  api?: ApiService;
  /** Édition : groupes d'envs où l'API tourne (version ou serveur live) ; absent = d'après ses versions. */
  liveGroups?: ApiEnvId[];
  /** Création : catégorie présélectionnée (Externe depuis l'onglet Externe). */
  defaultCategory?: ApiCategory;
  /** Création : les API chargées prouvent un CICD sans contrôle de santé (champ masqué). */
  healthCheckUnsupported?: boolean;
  /** CICD qui gère les instances : textes des instances et garde de la catégorie Externe. */
  instancesEnabled?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(api?.name ?? '');
  const [description, setDescription] = useState(api?.description ?? '');
  const [repository, setRepository] = useState(api?.repository ?? '');
  const [dockerImage, setDockerImage] = useState(api?.dockerImage ?? '');
  // Catégorie tenue par le CICD (une création part de "ig", sa valeur par défaut) : envoyée seulement si elle en diffère.
  const initialCategory: ApiCategory = api ? apiCategory(api) : 'ig';
  const [category, setCategory] = useState<ApiCategory>(api ? initialCategory : defaultCategory ?? 'ig');
  // Contrôle de santé : "port" par défaut, envoyé seulement s'il change. Affiché si le CICD le gère :
  // en édition, l'API le porte ; en création, sauf si la liste chargée prouve un CICD plus ancien.
  const initialHealthCheck: ApiHealthCheck = api?.healthCheck ?? 'port';
  const [healthCheck, setHealthCheck] = useState<ApiHealthCheck>(initialHealthCheck);
  const healthCheckShown = api ? api.healthCheck !== undefined : !healthCheckUnsupported;
  // Même droit que la répartition et les instances : le CICD répond 403 sans lui
  const user = useSessionStore((s) => s.user);
  const canSetHealthCheck = can(user, 'cicd.deploy.prod');
  const respondingOn = useRespondingEndpoints(projectId, api,
    !!api && initialHealthCheck === 'port' && healthCheck === 'process');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Même garde que le backend : l'explorateur Base de données a besoin d'un Mongo par env de jeu.
  const mongoBlocked = category === 'external' && !canBeExternal(dockerImage);
  // Base de données : la promotion l'ignore, changer son image demande de retaper son nom.
  const database = isDatabaseImage(dockerImage);
  const liveGroups = api ? liveGroupsProp ?? externalGroups(api) : [];
  // Une API externe n'a qu'une instance : le CICD refuse le passage tant qu'il en reste d'autres.
  const instanceCount = api ? apiInstances(api).length : 1;
  // Même garde que le CICD : une API externe n'a pas de répartition, un groupe sur « Aucune » ne la
  // recevrait jamais (seulement pour le passage d'une API de jeu).
  const noneBlocked = !!api && initialCategory !== 'external' && instanceCitations(api, NONE_INSTANCE).length > 0;
  const externalBlocked = instanceCount > 1 || noneBlocked;
  const renamed = !!api && name.trim() !== '' && name.trim() !== api.name;
  const valid = !!name.trim() && !!repository.trim() && !mongoBlocked && !(category === 'external' && externalBlocked);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        repository: repository.trim(),
        dockerImage: dockerImage.trim() || undefined,
        // Envoyée seulement si elle change (absente = "ig" côté backend).
        ...(category !== initialCategory ? { category } : {}),
        // Idem (absent = "port") : un PATCH sans lui garde cicd.view
        ...(healthCheckShown && healthCheck !== initialHealthCheck ? { healthCheck } : {}),
      };
      if (api) {
        await apiServicesService.update(projectId, api.id, payload);
      } else {
        const created = await apiServicesService.create(projectId, payload);
        // CICD plus ancien (liste vide, rien pour le détecter avant) : le réglage est ignoré sans erreur
        if (payload.healthCheck === 'process' && created.healthCheck === undefined) {
          alert(`${created.name} est créée, mais ce CICD ne gère pas encore le contrôle de santé : elle reste`
            + ' sondée sur son port. Repasse-la en « Process seulement » via « Éditer » une fois le CICD à jour.');
        }
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={api ? `Éditer ${api.name}` : 'Créer une API'}
      subtitle={instancesEnabled
        ? '1 API = 1 repo GitHub = 1 à 10 instances, chacune avec son serveur Ptero par env'
        : '1 API = 1 repo GitHub = 1 server Ptero par env'}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" onClick={submit} disabled={!valid || submitting} loading={submitting}>
          {api ? 'Sauvegarder' : 'Créer'}
        </Button>
      </>}
    >
      <div className={styles.form}>
        {error && <div className={styles.error}>{error}</div>}
        <label className={styles.field}>
          <span>Nom <em>requis</em></span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="EventAPI" />
          {renamed && instancesEnabled && (
            <div className={[styles.fieldHint, styles.fieldHintWarn].join(' ')}>
              Le nom donne les placeholders [[{placeholderSlug(api.name)}_HOST]] et [[{placeholderSlug(api.name)}_PORT]]
              {' '}des configs de mods : le changer les casse sur tous les serveurs
              {instanceCount > 1 ? ', pour toutes les instances' : ''}.
            </div>
          )}
        </label>
        <label className={styles.field}>
          <span>Description</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Repository GitHub <em>requis · org/nom</em></span>
          <input value={repository} onChange={(e) => setRepository(e.target.value)} placeholder="Paladium-Bedrock/EventAPI" />
        </label>
        <label className={styles.field}>
          <span>Image Docker template <em>tokens : {'{version}'} (sans v) · {'{versionRaw}'} (avec v)</em></span>
          <input value={dockerImage} onChange={(e) => setDockerImage(e.target.value)}
            placeholder="harbor.be.recube.gg/paladium-apis/event-api:{version}" />
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            Laissé vide → défaut <code>harbor.be.recube.gg/paladium-apis/&lt;nom-kebab&gt;:{'{version}'}</code>
          </span>
        </label>
        <label className={styles.field}>
          <span>Catégorie</span>
          <select value={category} onChange={(e) => setCategory(e.target.value as ApiCategory)}>
            <option value="ig">Jeu (IG)</option>
            <option value="external" disabled={externalBlocked && initialCategory !== 'external'}>Externe</option>
          </select>
          <div className={styles.fieldHint}>
            {category === 'external'
              ? "Une seule instance, sur un seul groupe d'environnements (Dev + Bêta, Pré-prod ou Prod). Listée dans l'onglet Externe, ignorée par la promotion."
              : database
                ? "Listée dans les onglets d'environnement. Base de données : la promotion ne la met pas à jour."
                : "Déployée sur tous les environnements et mise à jour par la promotion. Listée dans les onglets d'environnement."}
          </div>
          {database && (
            <div className={[styles.fieldHint, styles.fieldHintWarn].join(' ')}>
              Image de base de données : Release, « Déployer une version… » et la création d'un serveur demanderont de
              retaper le nom exact de l'API, car un saut de version peut l'empêcher de redémarrer sur ses données.
            </div>
          )}
          {instanceCount > 1 && (
            <div className={styles.fieldHint}>
              Une API externe n'a qu'une instance : supprime d'abord les instances en trop.
            </div>
          )}
          {noneBlocked && (
            <div className={styles.fieldHint}>
              Une API externe n'a pas de répartition : remets d'abord sur la Principale les groupes réglés sur
              « {NONE_INSTANCE_LABEL} » (Répartition).
            </div>
          )}
          {mongoBlocked && (
            <div className={[styles.fieldHint, styles.fieldHintDanger].join(' ')}>
              Une API MongoDB doit rester « Jeu (IG) » : la page Base de données lit son adresse sur chaque environnement.
            </div>
          )}
          {!mongoBlocked && category === 'external' && liveGroups.length > 1 && (
            <div className={[styles.fieldHint, styles.fieldHintWarn].join(' ')}>
              Elle tourne actuellement sur {liveGroups.map(apiGroupLabel).join(', ')} : supprime le serveur en trop
              pour n'en garder qu'un.
            </div>
          )}
        </label>
        {healthCheckShown && (
          <label className={styles.field}>
            <span>Contrôle de santé</span>
            <select value={healthCheck} onChange={(e) => setHealthCheck(e.target.value as ApiHealthCheck)}
              disabled={!canSetHealthCheck}>
              <option value="port">Port d'écoute (le CICD sonde ip:port de l'allocation)</option>
              <option value="process">Process seulement (worker sans port d'écoute, ex. consommateur RabbitMQ)</option>
            </select>
            <div className={styles.fieldHint}>
              {healthCheck === 'process'
                ? '« Erreur » seulement si le process redémarre en boucle ; la Release attend environ 1 min de marche sans redémarrage.'
                : "« Erreur » si l'API ne répond plus sur son allocation ou redémarre en boucle ; la Release attend que le port réponde."}
              {' '}Réglage propre à l'API de ce projet, pour toutes ses instances.
            </div>
            {/* Aussi pour une base déjà en process : le moteur écoute toujours sur son port */}
            {database && healthCheck === 'process' && (
              <div className={[styles.fieldHint, styles.fieldHintDanger].join(' ')}>
                Image de base de données : le moteur écoute sur son port, garde « Port d'écoute » pour
                qu'une panne soit signalée.
              </div>
            )}
            {healthCheck === 'process' && initialHealthCheck === 'port' && (
              <div className={[styles.fieldHint, styles.fieldHintWarn].join(' ')}>
                À réserver aux workers : une API HTTP qui n'écoute plus ne serait plus signalée.
                {respondingOn.length > 0 && (
                  <> Le CICD la voit répondre sur {joinFr(respondingOn)} : une panne de ce port ne serait plus vue.</>
                )}
              </div>
            )}
            {!canSetHealthCheck && (
              <div className={styles.fieldHint}>Permission cicd.deploy.prod requise pour changer ce réglage.</div>
            )}
          </label>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Deploy modal
// ============================================================
/**
 * Stub temporaire — utilisé par DevEnvDetail (feature dev envs pas encore rewire).
 * TODO Phase 4 : remplacer par la vraie modal quand les envs de dev seront branchés.
 */
export function CreateDevApiModal({ onClose }: { onClose: () => void; initialEnvId?: string }) {
  return (
    <Modal open onClose={onClose} title="Créer une API dev"
      subtitle="Feature dev envs pas encore développée"
      width="sm"
      footer={<Button variant="primary" onClick={onClose}>OK</Button>}
    >
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        La création d'APIs sur envs de dev arrive en Phase 4. En attendant, tu peux utiliser
        la création d'APIs standard (/apis) — elles sont directement déployables sur les 4 envs (dev/beta/preprod/prod).
      </p>
    </Modal>
  );
}

/**
 * Le backend renvoie une entrée par (instance, env demandé) ; dev et beta partagent le serveur
 * et reçoivent la même entrée : on n'en affiche qu'une, libellée « Dev + Bêta ». Un CICD plus
 * ancien les traitait séparément : si elles diffèrent, on garde les deux.
 */
function mergeGroupResults(rs: ApiDeployResultEntry[]): { entry: ApiDeployResultEntry; label: string }[] {
  const same = (a: ApiDeployResultEntry, b: ApiDeployResultEntry) =>
    (a.instance ?? DEFAULT_INSTANCE) === (b.instance ?? DEFAULT_INSTANCE)
    && a.ok === b.ok && !!a.skipped === !!b.skipped && !!a.needsCreate === !!b.needsCreate
    && a.newImage === b.newImage && a.serverName === b.serverName && a.error === b.error && a.warning === b.warning;
  const out: { entry: ApiDeployResultEntry; label: string }[] = [];
  for (const r of rs) {
    const twin = out.find((o) => apiGroupLabel(o.entry.environment) === apiGroupLabel(r.environment) && same(o.entry, r));
    if (twin) twin.label = apiGroupLabel(r.environment);
    else out.push({ entry: r, label: r.environment });
  }
  return out;
}

/** Configuration initiale d'un serveur à créer depuis la modale : source copiée et suffixe. */
interface CreateConfig { source: string; suffix: string }

function ApiDeployModal({
  projectId, api, initialEnv, groups, liveGroups: liveGroupsProp, instances: preselected, onClose, onDeployed,
}: {
  projectId: string;
  api: ApiService;
  /** Onglet d'où la modale est ouverte : son groupe est présélectionné (Dev + Bêta seulement). */
  initialEnv?: ApiEnvId;
  groups: ProjectGroup[];
  /** API externe : groupes d'envs où elle tourne (version ou serveur live) ; absent = d'après la ligne. */
  liveGroups?: ApiEnvId[];
  /** Instances cochées d'office (présentes sur l'env de l'onglet, ou une seule) ; absent = Principale. */
  instances?: string[];
  onClose: () => void;
  onDeployed: () => void;
}) {
  const [version, setVersion] = useState(api.latestAvailable ?? '');
  // Pré-prod et Prod ne sont jamais cochées d'office : il faut les choisir explicitement.
  const [selectedEnvs, setSelectedEnvs] = useState<Set<ApiEnvId>>(
    () => new Set(initialEnv && !ENVIRONMENT_BY_ID[initialEnv].requiresConfirmation ? apiEnvGroup(initialEnv) : []));
  const [selectedInstances, setSelectedInstances] = useState<Set<string>>(
    () => new Set(preselected && preselected.length > 0 ? preselected : [DEFAULT_INSTANCE]));
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ApiDeployResultEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ressources par défaut cohérentes pour un backend .NET stateless
  const [ramMb, setRamMb] = useState(1024);
  const [diskMb, setDiskMb] = useState(5000);
  const [cpuPct, setCpuPct] = useState(200);
  const [creatingKey, setCreatingKey] = useState<string | null>(null);
  const [createConfigs, setCreateConfigs] = useState<Record<string, CreateConfig>>({});
  // Base de données : deploy et création de serveur attendent son nom retapé, envoyé au CICD
  const database = isDatabaseApi(api);
  const [dbTyped, setDbTyped] = useState('');
  const dbOk = !database || databaseNameMatches(api, dbTyped);
  const confirmDatabase = database && dbOk ? api.name : undefined;

  const external = isExternalApi(api);
  const liveGroups = liveGroupsProp ?? externalGroups(api);
  const all = apiInstances(api);
  // Cases par instance : API à plusieurs instances d'un CICD qui les gère
  const showInstances = instancesSupported(api) && all.length > 1;
  const chosen = showInstances ? all.filter((i) => selectedInstances.has(i.key)) : all.slice(0, 1);
  const tabEnv = canonicalApiEnv(initialEnv ?? 'dev');
  const target = normalizeVersion(version) || normalizeVersion(api.latestAvailable);
  const currentOn = (inst: ApiInstance, e: ApiEnvId) => versionOn(inst, e);
  // Verrouillé « Déployée vX » seulement si toutes les instances cochées y sont déjà à jour.
  const upToDateOn = (e: ApiEnvId) => chosen.length > 0 && chosen.every((i) => sameVersion(currentOn(i, e), target));

  // API externe : un seul groupe d'envs, choisi par un bouton radio ; déjà déployée, celui où elle tourne.
  const allowedGroups = external && liveGroups.length > 0 ? liveGroups : null;

  /** Raison pour laquelle l'env n'est pas sélectionnable, ou null. */
  const envLock = (e: ApiEnvId): string | null => {
    const current = chosen[0] ? currentOn(chosen[0], e) : undefined;
    if (current && upToDateOn(e)) return `Déployée v${normalizeVersion(current)}`;
    if (allowedGroups && !allowedGroups.includes(canonicalApiEnv(e))) {
      return `Externe · sur ${allowedGroups.map(apiGroupLabel).join(', ')}`;
    }
    return null;
  };
  const effectiveEnvs = API_ENVS.filter((e) => selectedEnvs.has(e) && !envLock(e));
  const effectiveGroups = API_ENV_GROUPS.filter((g) => apiEnvGroup(g).some((e) => effectiveEnvs.includes(e)));

  // Dev et Bêta se cochent ensemble : même serveur API, un seul deploy côté backend.
  const toggle = (e: ApiEnvId) => {
    setSelectedEnvs((s) => {
      const n = new Set(s);
      const on = !n.has(e);
      for (const m of apiEnvGroup(e)) { if (on) n.add(m); else n.delete(m); }
      return n;
    });
  };
  // API externe : un seul groupe à la fois (Dev + Bêta compte pour un).
  const pickGroup = (g: ApiEnvId) => setSelectedEnvs(new Set(apiEnvGroup(g)));
  const toggleInstance = (key: string) => {
    setSelectedInstances((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  // Fermer après un deploy réussi recharge la liste (sinon elle garderait l'ancienne version).
  const close = () => (results?.some((r) => r.ok) ? onDeployed() : onClose());

  const submit = async () => {
    if (!dbOk) return;
    const sensitive = effectiveGroups.filter((g) => ENVIRONMENT_BY_ID[g].requiresConfirmation);
    const who = showInstances ? ` (${joinFr(chosen.map(instanceLabel))})` : '';
    // Base de données : le nom retapé dans la modale tient lieu de cette confirmation
    if (!database && sensitive.length > 0
      && !confirm(`Déployer ${api.name}${who} en v${target || '?'} sur ${sensitive.map(apiGroupLabel).join(', ')} : l'image sera changée et prise au prochain redémarrage (y compris un redémarrage automatique après crash). Continuer ?`)) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiServicesService.deploy(projectId, api.id, {
        environments: effectiveEnvs,
        version: version || undefined,
        instances: instancesParam(api, chosen.map((i) => i.key)),
        confirmDatabase,
      });
      setResults(res.results);
      // Ne referme (succès) que si au moins un déploiement a réellement réussi et qu'aucun n'a échoué.
      // Si tout est « skip » (env non configuré), on garde la modale ouverte pour que l'utilisateur
      // comprenne que rien n'a été déployé (et pourquoi). Idem si le backend remonte un avertissement.
      const anyOk = res.results.some((r) => r.ok);
      const noFail = res.results.every((r) => r.ok || r.skipped);
      const noWarning = res.results.every((r) => !r.warning);
      if (anyOk && noFail && noWarning) setTimeout(onDeployed, 1200);
    } catch (e) {
      setError(databaseConfirmErrorMessage(e, api) ?? (e instanceof Error ? e.message : 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Sources de copie pour le serveur à créer : les autres instances avec un serveur sur l'env.
   * Aucune pour la Principale, qui part de la config de l'image (le CICD refuse de la copier).
   */
  const copySources = (instKey: string, env: string) => instKey === DEFAULT_INSTANCE ? []
    : all.filter((i) => i.key !== instKey && hasServer(i, env as ApiEnvId));
  const configOf = (instKey: string, env: string): CreateConfig => {
    const k = pairKey(instKey, canonicalApiEnv(env as ApiEnvId));
    const sources = copySources(instKey, env);
    return createConfigs[k] ?? {
      source: instKey !== DEFAULT_INSTANCE && sources.some(isDefaultInstance) ? DEFAULT_INSTANCE : '',
      suffix: instKey === DEFAULT_INSTANCE ? '' : `_${instKey}`,
    };
  };
  const setConfig = (instKey: string, env: string, patch: Partial<CreateConfig>) => {
    const k = pairKey(instKey, canonicalApiEnv(env as ApiEnvId));
    setCreateConfigs((m) => ({ ...m, [k]: { ...configOf(instKey, env), ...patch } }));
  };

  const createFresh = async (entry: ApiDeployResultEntry) => {
    if (!dbOk) return;
    // Dev et Bêta : un seul serveur, la création règle les deux entrées (de la même instance).
    const env = entry.environment;
    const instKey = entry.instance ?? DEFAULT_INSTANCE;
    const group = apiGroupLabel(env);
    const inGroup = (r: ApiDeployResultEntry) => apiGroupLabel(r.environment) === group
      && (r.instance ?? DEFAULT_INSTANCE) === instKey;
    const cfg = configOf(instKey, env);
    const copy = !!cfg.source && copySources(instKey, env).some((s) => s.key === cfg.source);
    setCreatingKey(pairKey(instKey, env as ApiEnvId));
    try {
      const res = await apiServicesService.createServer(projectId, api.id, {
        environment: env,
        version: version || undefined,
        memoryMb: ramMb, diskMb, cpuPct,
        ...(instKey !== DEFAULT_INSTANCE ? { instance: instKey } : {}),
        ...(copy ? { copyConfigFrom: cfg.source, databaseSuffix: cfg.suffix.trim() || undefined } : {}),
        confirmDatabase,
      });
      const copyNote = res.copyConfig && !res.copyConfig.ok
        ? `Configuration manuelle requise : ${res.copyConfig.error ?? 'copie impossible'}`
        : res.copyConfig?.warning;
      const warning = [res.warning, copyNote].filter(Boolean).join(' · ') || undefined;
      // Autres serveurs encore à créer (autre instance ou autre env) : la modale reste ouverte
      const othersToCreate = (results ?? []).some((r) => r.needsCreate && !inGroup(r));
      // API sans instances (projet sans groupes de jeu, ou CICD plus ancien) : comme avant les instances
      const legacy = !instancesSupported(api);
      // Remplace le résultat "needsCreate" par un ok/ko
      setResults((prev) => prev?.map((r) => inGroup(r)
        ? { ...r, ok: res.ok, needsCreate: false, newImage: res.newImage,
            serverName: res.serverName, error: res.error, ...(legacy ? {} : { warning }) }
        : r) ?? null);
      if (res.ok && (legacy || (!warning && !othersToCreate))) setTimeout(onDeployed, 1500);
    } catch (e) {
      setResults((prev) => prev?.map((r) => inGroup(r)
        ? { ...r, ok: false, needsCreate: false, error: databaseConfirmErrorMessage(e, api) ?? (e instanceof Error ? e.message : 'Erreur') }
        : r) ?? null);
    } finally {
      setCreatingKey(null);
    }
  };

  const hasNeedsCreate = results?.some((r) => r.needsCreate);
  const shownResults = results ? mergeGroupResults(results) : null;
  const resultLabel = (r: ApiDeployResultEntry, envLabel: string) => {
    if (!showInstances) return envLabel;
    const inst = findInstance(api, r.instance ?? DEFAULT_INSTANCE);
    return `${inst ? instanceLabel(inst) : r.instance} · ${envLabel}`;
  };

  return (
    <Modal
      open onClose={close}
      title={`Déployer ${api.name}`}
      subtitle={`Version disponible : ${api.latestAvailable ?? '—'}`}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={close} disabled={submitting}>Fermer</Button>
        <Button variant="primary" onClick={submit} disabled={submitting || effectiveEnvs.length === 0 || chosen.length === 0 || !dbOk}
          loading={submitting} leftIcon={<Icon name="rocket" size={12} />}>
          {effectiveGroups.length > 0 ? `Déployer sur ${effectiveGroups.map(apiGroupLabel).join(', ')}` : 'Déployer'}
        </Button>
      </>}
    >
      <div className={styles.form}>
        {error && <div className={styles.error}>{error}</div>}
        <label className={styles.field}>
          <span>Version cible <em>(vide = latestAvailable)</em></span>
          <input value={version} onChange={(e) => setVersion(e.target.value)}
            placeholder={api.latestAvailable ?? '0.1.0'} />
        </label>
        {showInstances && (
          <div className={styles.field}>
            <span>Instances</span>
            <div className={styles.envCheckRow}>
              {all.map((inst) => {
                const on = selectedInstances.has(inst.key);
                const v = versionOn(inst, tabEnv);
                return (
                  <label key={inst.key}
                    className={[styles.envCheck, on && styles.envCheckActive].filter(Boolean).join(' ')}
                    style={{ flexWrap: 'wrap', rowGap: 2 }}>
                    <input type="checkbox" checked={on} disabled={submitting} onChange={() => toggleInstance(inst.key)} />
                    <InstanceTag inst={inst} served={servedGroupsOf(api, inst, tabEnv, groups)} forceLabel />
                    {v && <Badge tone="info">v{v}</Badge>}
                    {!hasControllableServer(inst, tabEnv) && (
                      <span className={styles.envCheckNote}>pas de serveur sur {apiGroupLabel(tabEnv)}</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className={styles.fieldHint}>
              Chaque instance cochée est déployée sur chaque environnement coché. Versions et drapeaux : {apiGroupLabel(tabEnv)}.
            </div>
          </div>
        )}
        {external ? (
          <div className={styles.field}>
            <span>Groupe d'environnements</span>
            <div className={styles.envGroupRow} role="radiogroup" aria-label="Groupe d'environnements">
              {API_ENV_GROUPS.map((g) => {
                const lock = envLock(g);
                const active = selectedEnvs.has(g) && !lock;
                return (
                  <label key={g}
                    className={[styles.envCheck, active && styles.envCheckActive, lock && styles.envCheckDisabled].filter(Boolean).join(' ')}
                    title={lock ?? (apiEnvGroup(g).length > 1 ? 'Dev et Bêta partagent le même serveur API' : undefined)}>
                    <input type="radio" name={`api-group-${api.id}`} checked={active} disabled={!!lock || submitting}
                      onChange={() => pickGroup(g)} />
                    {apiEnvGroup(g).map((e) => <EnvBadge key={e} env={e} size="sm" />)}
                    {lock && <span className={styles.envCheckNote}>{lock}</span>}
                  </label>
                );
              })}
            </div>
            <div className={styles.fieldHint}>
              {liveGroups.length > 0
                ? `API externe : une seule instance, qui tourne sur ${liveGroups.map(apiGroupLabel).join(', ')}. Elle ne se déploie pas sur un autre groupe tant que ce serveur existe.`
                : "API externe : une seule instance, sur le seul groupe choisi ici. Elle ne pourra pas être déployée sur un autre groupe tant que ce serveur existera."}
            </div>
          </div>
        ) : (
          <div className={styles.field}>
            <span>Environnements</span>
            <div className={styles.envCheckRow}>
              {API_ENVS.map((e) => {
                const lock = envLock(e);
                const active = selectedEnvs.has(e) && !lock;
                return (
                  <label key={e}
                    className={[styles.envCheck, active && styles.envCheckActive, lock && styles.envCheckDisabled].filter(Boolean).join(' ')}
                    title={lock ?? (apiEnvGroup(e).length > 1 ? 'Dev et Bêta partagent le même serveur API : cochés ensemble' : undefined)}>
                    <input type="checkbox" checked={active} disabled={!!lock || submitting} onChange={() => toggle(e)} />
                    <EnvBadge env={e} size="sm" />
                    {lock && <span className={styles.envCheckNote}>{lock}</span>}
                  </label>
                );
              })}
            </div>
            <div className={styles.fieldHint}>
              Dev et Bêta partagent les mêmes serveurs API : cocher l'un coche l'autre, un seul déploiement est fait.
            </div>
          </div>
        )}

        {database && (
          <DatabaseConfirmField
            api={api}
            summary={<>
              Déployer v{target || '?'}{effectiveGroups.length > 0 ? ` sur ${effectiveGroups.map(apiGroupLabel).join(', ')}` : ''}
              {' '}change l'image de <code>{api.name}</code>, prise au prochain redémarrage (y compris un redémarrage
              automatique après crash) ; « Créer » installe un serveur sur cette image.
            </>}
            value={dbTyped}
            onChange={setDbTyped}
            disabled={submitting || creatingKey !== null}
          />
        )}

        {hasNeedsCreate && (
          <div className={styles.hintBox} style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)', borderStyle: 'solid' }}>
            <Icon name="warn" size={12} />
            <span>
              Certains envs n'ont pas de serveur Ptero. Renseigne les ressources ci-dessous
              puis clique <strong>Créer</strong> pour chacun.
            </span>
          </div>
        )}

        {hasNeedsCreate && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <label className={styles.field}>
              <span>RAM (Mo)</span>
              <input type="number" value={ramMb} onChange={(e) => setRamMb(Number(e.target.value) || 0)} />
            </label>
            <label className={styles.field}>
              <span>Disk (Mo)</span>
              <input type="number" value={diskMb} onChange={(e) => setDiskMb(Number(e.target.value) || 0)} />
            </label>
            <label className={styles.field}>
              <span>CPU (%) <em>100 = 1 core</em></span>
              <input type="number" value={cpuPct} onChange={(e) => setCpuPct(Number(e.target.value) || 0)} />
            </label>
          </div>
        )}

        {shownResults && (
          <div className={styles.results}>
            {shownResults.map(({ entry: r, label }) => {
              const instKey = r.instance ?? DEFAULT_INSTANCE;
              const rowKey = pairKey(instKey, r.environment as ApiEnvId);
              const sources = r.needsCreate && instancesSupported(api) ? copySources(instKey, r.environment) : [];
              const cfg = configOf(instKey, r.environment);
              const suffixError = cfg.source && cfg.suffix.trim() && !DB_SUFFIX_PATTERN.test(cfg.suffix.trim())
                ? 'Lettres, chiffres, _ et -, 32 caractères au plus' : null;
              return (
                <Fragment key={rowKey}>
                  <div className={[
                    styles.resultRow,
                    r.ok ? styles.resultOk : r.skipped ? styles.resultSkip :
                    r.needsCreate ? styles.resultSkip : styles.resultKo,
                  ].join(' ')}>
                    <Icon name={r.ok ? 'check' : r.skipped || r.needsCreate ? 'warn' : 'x'} size={12} />
                    <strong>{resultLabel(r, label)}</strong>
                    <span style={{ flex: 1 }}>
                      {r.ok && <>→ {r.serverName} : <code>{r.newImage}</code></>}
                      {r.skipped && <>SKIP : {r.error}</>}
                      {r.needsCreate && <>Pas de serveur : {r.error}</>}
                      {!r.ok && !r.skipped && !r.needsCreate && <>KO : {r.error}</>}
                    </span>
                    {r.needsCreate && (
                      <Button variant="primary" size="sm" leftIcon={<Icon name="rocket" size={11} />}
                        loading={creatingKey === rowKey}
                        disabled={creatingKey !== null || !!suffixError || !dbOk}
                        onClick={() => void createFresh(r)}>
                        Créer
                      </Button>
                    )}
                  </div>
                  {r.needsCreate && sources.length > 0 && (
                    <div className={styles.results} style={{ marginTop: 0, paddingLeft: 12 }}>
                      <CopyConfigFields api={api} envId={r.environment as ApiEnvId} sources={sources}
                        source={cfg.source} onSource={(s) => setConfig(instKey, r.environment, { source: s })}
                        suffix={cfg.suffix} onSuffix={(s) => setConfig(instKey, r.environment, { suffix: s })}
                        suffixError={suffixError} />
                    </div>
                  )}
                  {r.warning && (
                    <div className={[styles.resultRow, styles.resultWarn].join(' ')}>
                      <Icon name="warn" size={12} />
                      <span style={{ flex: 1 }}>{r.warning}</span>
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Override binaire (env DEV, hors versionning)
// ============================================================
/**
 * L'API compilee vit dans l'image Docker (/api/<Name>.dll), pas dans le volume : le file
 * explorer Ptero ne voit que appsettings.json, scripts/ et logs/. Remplacer le binaire passe
 * donc par un override depose dans /home/container/override, que le backend applique sur /api
 * au demarrage via un hook appele depuis scripts/prerun.sh.
 *
 * Toute la mecanique (depot, hook, bascule, suppression) vit cote backend : ce composant lit
 * un etat et declenche des operations, il ne connait aucune convention de nommage de fichier.
 */
function ApiOverrideModal({
  projectId, api, instance, onClose, onDeployed,
}: {
  projectId: string;
  api: ApiService;
  /** Instance dont le serveur dev reçoit l'override (Principale par défaut). */
  instance: ApiInstance;
  onClose: () => void;
  onDeployed: () => void;
}) {
  const inst = instance.key;
  const [state, setState] = useState<ApiOverrideState | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [restart, setRestart] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setState(await apiOverrideService.state(projectId, api.id, inst));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lecture de l'etat de l'override KO");
    } finally {
      setLoading(false);
    }
  }, [projectId, api.id, inst]);

  useEffect(() => { void refresh(); }, [refresh]);

  /** Enveloppe commune : le backend renvoie toujours son deroule, on l'affiche tel quel. */
  const run = async (label: string, op: () => Promise<ApiOverrideResult>) => {
    setBusy(label);
    setError(null);
    setLog([]);
    try {
      const r = await op();
      setLog(r.steps ?? []);
      if (r.state) setState(r.state); else await refresh();
      if (!r.ok) setError(r.error ?? 'Operation en echec');
      else setFile(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  const disabled = busy !== null || loading;
  const active = state?.active ?? false;
  const present = state?.present ?? false;

  return (
    <Modal
      open onClose={onClose}
      title={`Override binaire · ${instanceDisplayName(api, instance)}`}
      subtitle="Faire tourner un build local a la place de l'image, sur l'env dev uniquement"
      width="md"
      footer={<Button variant="ghost" onClick={onDeployed} disabled={busy !== null}>Fermer</Button>}
    >
      <div className={styles.form}>
        {error && <div className={styles.error}>{error}</div>}

        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px',
          background: 'var(--color-background-secondary)', border: '1px dashed var(--color-border)',
          borderRadius: 8, fontSize: 12, color: 'var(--color-text-muted)',
        }}>
          <Icon name="warn" size={12} />
          <span>
            Env <strong>dev</strong>, hors versionning. Le fichier est depose dans le volume et
            recopie sur l'API compilee a chaque demarrage. La version git reste dans l'image :
            desactiver ou supprimer l'override suffit a y revenir. Ne change pas la version
            deployee affichee.
          </span>
        </div>

        {state && !state.hookInstalled && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px',
            background: 'var(--color-warning-soft, rgba(245,158,11,.12))',
            border: '1px solid var(--color-warning, #f59e0b)', borderRadius: 8, fontSize: 12,
          }}>
            <Icon name="warn" size={12} />
            <span style={{ flex: 1 }}>
              Le hook n'est pas installe : scripts/prerun.sh ne l'appelle pas, donc l'override
              ne sera pas applique au demarrage.
            </span>
            <Button variant="secondary" size="sm" disabled={disabled}
              onClick={() => void run('Installation du hook...', () => apiOverrideService.installHook(projectId, api.id, inst))}>
              Installer le hook
            </Button>
          </div>
        )}

        <div className={styles.field}>
          <span>Source active</span>
          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Detection...</div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge tone={active ? 'muted' : 'info'}>
                Git{state?.gitVersion ? ` v${state.gitVersion}` : ''}
              </Badge>
              <Badge tone={active ? 'warning' : 'muted'}>Override manuel</Badge>
              <span style={{ flex: 1 }} />
              <Button variant={active ? 'ghost' : 'softPrimary'} size="sm"
                disabled={disabled || !present || !active}
                leftIcon={<Icon name="git-branch" size={11} />}
                onClick={() => void run('Bascule sur la version git...', () => apiOverrideService.toggle(projectId, api.id, false, restart, inst))}>
                Basculer sur Git
              </Button>
              <Button variant={active ? 'softPrimary' : 'ghost'} size="sm"
                disabled={disabled || !present || active}
                leftIcon={<Icon name="package" size={11} />}
                onClick={() => void run('Bascule sur l\'override...', () => apiOverrideService.toggle(projectId, api.id, true, restart, inst))}>
                Basculer sur override
              </Button>
            </div>
          )}
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            {!present
              ? "Aucun override depose : l'API tourne sur l'image publiee par le CI."
              : active
                ? "L'override est applique sur /api a chaque demarrage."
                : "Override en reserve (parke) : l'API tourne sur l'image."}
          </span>
        </div>

        {present && state?.files && state.files.length > 0 && (
          <div className={styles.field}>
            <span>Fichiers deposes <em>lus sur le serveur</em></span>
            <div className={styles.results}>
              {state.files.map((f) => (
                <div key={f.name} className={styles.resultRow}>
                  <Icon name={f.isFile ? 'package' : 'layers'} size={12} />
                  <span style={{ flex: 1, wordBreak: 'break-all' }}>{f.name}</span>
                  {f.isFile && <span style={{ color: 'var(--color-text-muted)' }}>{formatBytes(f.size)}</span>}
                </div>
              ))}
            </div>
            {state.lastUploadName && (
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                Dernier depot : {state.lastUploadName}
                {state.lastUploadSize ? ` (${formatBytes(state.lastUploadSize)})` : ''}
                {state.lastUploadSha256 ? ` · sha256 ${state.lastUploadSha256.slice(0, 12)}…` : ''}
                {state.lastActionByName ? ` · par ${state.lastActionByName}` : ''}
                {state.lastActionAt ? ` le ${new Date(state.lastActionAt).toLocaleString('fr-FR')}` : ''}
              </span>
            )}
          </div>
        )}

        <label className={styles.field}>
          <span>Deposer un build <em>.dll, .pdb, *.deps.json, ou archive publish .zip / .tar.gz</em></span>
          <input type="file" accept=".dll,.pdb,.json,.zip,.gz,.tgz,.tar" disabled={disabled}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        {file && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            <Icon name="package" size={12} />
            <span style={{ flex: 1, wordBreak: 'break-all' }}>{file.name}</span>
            <span style={{ color: 'var(--color-text-muted)' }}>{formatBytes(file.size)}</span>
          </div>
        )}
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          Une dll seule suffit quand les dependances NuGet n'ont pas bouge. Sinon, deposer
          l'archive du dossier publish (dll + deps.json + runtimeconfig + dependances).
        </span>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={restart} disabled={disabled}
            onChange={(e) => setRestart(e.target.checked)} />
          <span>Redemarrer le serveur dev apres l'operation (sinon applique au prochain demarrage)</span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Button variant="softDanger" size="sm" disabled={disabled || !present}
            leftIcon={<Icon name="x" size={11} />}
            onClick={() => void run("Suppression de l'override...", () => apiOverrideService.clear(projectId, api.id, restart, inst))}>
            Supprimer l'override
          </Button>
          <Button variant="primary"
            disabled={disabled || !file}
            loading={busy?.startsWith('Depot') ?? false}
            leftIcon={<Icon name="rocket" size={12} />}
            onClick={() => file && void run('Depot de l\'override...', () => apiOverrideService.deploy(projectId, api.id, file, restart, inst))}>
            Deployer l'override
          </Button>
        </div>

        {(busy || log.length > 0) && (
          <div className={styles.results}>
            {busy && (
              <div className={styles.resultRow}>
                <Icon name="refresh" size={12} />
                <span style={{ flex: 1 }}>{busy}</span>
              </div>
            )}
            {log.map((l, i) => (
              <div key={i} className={styles.resultRow}>
                <Icon name="chevron-right" size={12} />
                <span style={{ flex: 1 }}>{l}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  if (n < 1024 * 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + ' MB';
  return (n / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}
